import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Activity, AlertTriangle, Ban, CalendarClock, Camera, ClipboardCheck, CreditCard, Download, Flag, RefreshCw, Smile, Stethoscope, Undo2, WalletCards } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import FeatureAccessGate from "@/components/billing/FeatureAccessGate";
import { supabase } from "@/lib/supabaseClient";
import { buildOrthoVisitIdempotencyKey, orthoStageForVisitType } from "@/components/ortho/record/orthoVisitPayload";
import OrthoVisitPhotos from "@/components/ortho/record/OrthoVisitPhotos";
import OrthoWireGaugeSelect from "@/components/ortho/record/OrthoWireGaugeSelect";
import {
  formatWireSelection,
  wireSelectionFromVisit,
  wireSelectionPayload,
} from "@/components/ortho/record/wireRecording";
import { uploadPatientFile } from "@/lib/patientFilesApi";
import { formatDoctorName } from "@/lib/doctorName";
import {
  nextOrthoAdjustmentNumber,
  orthoAdjustmentNumber,
  orthoEventLabel,
} from "@/lib/orthoVisitSemantics";
import { buildOrthoContractPdf, pdfFilename } from "@/lib/treatmentPlanPdf";
import { clinicSettingsWithLogo } from "@/lib/treatmentPlanDelivery";
import {
  calculateAgeFromDob,
  getDoctorProfileForContract,
  getOrthoContractBlock,
  getOrthoDoctorProfileBlock,
  getOrthoPlanCreationBlock,
  getOrthoVisitStartBlock,
  isPatientMinor,
} from "@/lib/orthoContractGuards";

const CASE_TYPES = [
  { value: "fixed", label: "Fixed" },
  { value: "aligner", label: "Aligner" },
  { value: "removable", label: "Removable" },
];

const ORTHO_BILLING_MODELS = [
  {
    value: "per_visit_divisor",
    label: "Per-visit (divisor)",
    desc: "Formula: (package price - paid before Dental OS) ÷ remaining visits = charge per adjustment.",
  },
  {
    value: "monthly_recurring",
    label: "Monthly recurring",
    desc: "Formula: package balance ÷ fixed monthly amount = months to pay. Visits do not trigger charges.",
  },
  {
    value: "flat_per_visit",
    label: "Flat per-visit",
    desc: "Formula: flat fee × completed adjustment visits = total collected. No package balance.",
  },
  {
    value: "milestone",
    label: "Milestone-based",
    desc: "Formula: sum of remaining milestones = future amount due. Charges happen at each milestone.",
  },
  {
    value: "lump_sum",
    label: "Lump sum",
    desc: "Formula: total paid upfront = package paid in full. Future ortho visits are clinical only.",
  },
];

const VISIT_PICKERS = [12, 18, 24, 30, 36];
const COMPLAINTS = ["Crowding", "Spacing", "Class II", "Class III", "Deep bite", "Crossbite", "Aesthetic-only"];
const INPUT_CLASS = "h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100";
const CASE_TYPE_TUTORIAL_IDS = {
  fixed: "ortho-import-case-type-fixed",
  aligner: "ortho-import-case-type-aligner",
  removable: "ortho-import-case-type-removable",
};
const BILLING_MODEL_TUTORIAL_IDS = {
  per_visit_divisor: "ortho-import-billing-model-per-visit-divisor",
  monthly_recurring: "ortho-import-billing-model-monthly-recurring",
  flat_per_visit: "ortho-import-billing-model-flat-per-visit",
  milestone: "ortho-import-billing-model-milestone",
  lump_sum: "ortho-import-billing-model-lump-sum",
};
const SYSTEM_CATEGORIES_BY_CASE = {
  fixed: ["fixed_metal", "fixed_ceramic", "self_ligating"],
  aligner: ["aligner"],
  removable: ["removable"],
};
const ACTIVE_ORTHO_CASE_STATUSES = new Set([
  "active_pending_billing_setup",
  "consultation_pending_acceptance",
  "active_pending_bonding",
  "active",
  "delinquent",
  "paused",
  "retention",
]);
const CLOSED_ORTHO_CASE_STATUSES = new Set(["completed", "abandoned", "cancelled"]);

function isActiveOrthoCaseStatus(status) {
  return ACTIVE_ORTHO_CASE_STATUSES.has(String(status || ""));
}

const ORTHO_TRANSFER_DETAIL_STAGES = new Set(["owner", "money", "finish"]);

function initialOrthoImportForm(doctors, tutorialStage = "") {
  const base = {
    billing_model: "per_visit_divisor",
    case_type: "fixed",
    system_id: "",
    primary_doctor_id: doctors.find((d) => hasReadableDoctorName(d))?.id || doctors[0]?.id || "",
    original_doctor_unknown: false,
    migration_started_at: "",
    status: "active",
    package_price: "",
    original_price_known: true,
    estimated_remaining_balance: "",
    payment_history_known: true,
    migration_payment_status: "current",
    migration_payment_notes: "",
    total_paid_prior: "",
    estimated_remaining_visits: "12",
    per_visit_charge: "",
    monthly_amount: "",
    monthly_due_day: "",
    next_monthly_due_date: "",
    milestone_amount: "",
    milestone_due_condition: "",
    milestone_due_date: "",
    approximate_visit_count: "",
    last_visit_date: "",
    migration_data_quality: "partial",
    migration_notes: "",
    historical_payment_notes: "",
    historical_visit_notes: "",
    notes: "",
  };

  if (tutorialStage === "owner") return base;
  if (tutorialStage === "money") {
    return { ...base, migration_started_at: "2025-01-15" };
  }
  if (tutorialStage === "finish") {
    return {
      ...base,
      migration_started_at: "2025-01-15",
      package_price: "85000",
      total_paid_prior: "30000",
      estimated_remaining_visits: "11",
      per_visit_charge: "5000",
      approximate_visit_count: "7",
      last_visit_date: "2026-04-20",
      migration_notes: "Transferred from previous clinic; records are partial.",
      migration_payment_notes: "Current based on the old clinic ledger.",
      historical_payment_notes: "Old receipts show PHP 30,000 paid before transfer.",
      historical_visit_notes: "Patient reports seven adjustment visits.",
      notes: "Confirm records before the next adjustment.",
    };
  }
  return base;
}

function OrthoPanelBody({ patientId, patient, tutorialDemoData = null }) {
  const { clinicRole, user, activeClinic } = useAuth();
  const { activeBranchId } = useBranch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get("appointment_id");
  const tutorialStage = tutorialDemoData ? (searchParams.get("stage") || "open") : "";
  const [rows, setRows] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patientDoctorDefaults, setPatientDoctorDefaults] = useState({
    loaded: false,
    owningDentistId: null,
    preferredDoctorId: null,
  });
  const [systems, setSystems] = useState([]);
  const [wireOptions, setWireOptions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [clinicForPdf, setClinicForPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(ORTHO_TRANSFER_DETAIL_STAGES.has(tutorialStage));
  const [setupCase, setSetupCase] = useState(null);
  const [visitCase, setVisitCase] = useState(null);
  const [pauseCase, setPauseCase] = useState(null);
  const [reconcileCase, setReconcileCase] = useState(null);
  const [retainerSetupCase, setRetainerSetupCase] = useState(null);
  const [cancelCase, setCancelCase] = useState(null);
  const [cancelDebondingCase, setCancelDebondingCase] = useState(null);
  const [paymentOnlyCase, setPaymentOnlyCase] = useState(null);
  const [panelView, setPanelView] = useState("cases");

  useEffect(() => {
    if (tutorialDemoData) setImportOpen(ORTHO_TRANSFER_DETAIL_STAGES.has(tutorialStage));
  }, [tutorialDemoData, tutorialStage]);
  const planCreationBlock = useMemo(() => getOrthoPlanCreationBlock({ patient }), [patient]);
  const activeCaseRows = useMemo(() => (
    rows.filter((row) => isActiveOrthoCaseStatus(row?.case?.status))
  ), [rows]);
  const activeCaseBlockMessage = activeCaseRows.length > 1
    ? "This patient already has multiple active Ortho cases. Resolve the duplicate cases before starting or importing another."
    : activeCaseRows.length === 1
      ? "This patient already has an active Ortho case. Complete, cancel, or abandon it before starting another."
      : null;
  const creationBlockMessage = activeCaseBlockMessage || planCreationBlock?.message || null;
  const defaultStartDoctor = useMemo(() => resolveOrthoPrimaryDoctor({
    doctors,
    clinicRole,
    userId: user?.id,
    patientDoctorDefaults,
  }), [clinicRole, doctors, patientDoctorDefaults, user?.id]);
  const startDoctorProfileBlock = useMemo(() => {
    if (loading || error || !patientDoctorDefaults.loaded || activeCaseBlockMessage || planCreationBlock) return null;
    return getOrthoDoctorProfileBlock(defaultStartDoctor);
  }, [activeCaseBlockMessage, defaultStartDoctor, error, loading, patientDoctorDefaults.loaded, planCreationBlock]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (tutorialDemoData) {
      setRows(Array.isArray(tutorialDemoData.rows) ? tutorialDemoData.rows : []);
      setDoctors(Array.isArray(tutorialDemoData.doctors) ? tutorialDemoData.doctors : []);
      setPatientDoctorDefaults({ loaded: true, owningDentistId: null, preferredDoctorId: null });
      setSystems(Array.isArray(tutorialDemoData.systems) ? tutorialDemoData.systems : []);
      setWireOptions(Array.isArray(tutorialDemoData.wireOptions) ? tutorialDemoData.wireOptions : []);
      setSettings(tutorialDemoData.settings || null);
      setClinicForPdf(tutorialDemoData.clinic || activeClinic || null);
      setLoading(false);
      return;
    }
    const [caseRes, doctorRes, systemRes, wireRes, settingsRes, clinicSettingsRes, patientDoctorRes] = await Promise.all([
      supabase.rpc("list_patient_ortho_cases", { p_patient_id: patientId }),
      supabase.rpc("list_ortho_doctor_records"),
      supabase.rpc("list_ortho_systems", { p_include_inactive: false }),
      supabase.rpc("list_ortho_wire_options", { p_include_inactive: false }),
      supabase.rpc("get_ortho_settings"),
      supabase.rpc("get_clinic_settings"),
      supabase
        .from("patients")
        .select("owning_dentist_id, preferred_doctor_id")
        .eq("id", patientId)
        .maybeSingle(),
    ]);
    setLoading(false);
    if (caseRes.error) {
      setError(caseRes.error.message || "Could not load ortho cases.");
      return;
    }
    const caseRows = Array.isArray(caseRes.data) ? caseRes.data : [];
    const timelineEntries = await Promise.all(caseRows.map(async (caseRow) => {
      const caseId = caseRow?.case?.id;
      if (!caseId) return [caseId, []];
      const timelineRes = await supabase.rpc("get_ortho_case_timeline", { p_case_id: caseId });
      return [caseId, timelineRes.error ? [] : (Array.isArray(timelineRes.data) ? timelineRes.data : [])];
    }));
    const timelineByCaseId = Object.fromEntries(timelineEntries.filter(([caseId]) => caseId));
    setRows(caseRows.map((caseRow) => ({
      ...caseRow,
      timeline: timelineByCaseId[caseRow?.case?.id] || [],
    })));
    if (!doctorRes.error) setDoctors(Array.isArray(doctorRes.data) ? doctorRes.data : []);
    setPatientDoctorDefaults({
      loaded: true,
      owningDentistId: patientDoctorRes.error ? null : patientDoctorRes.data?.owning_dentist_id ?? null,
      preferredDoctorId: patientDoctorRes.error ? null : patientDoctorRes.data?.preferred_doctor_id ?? null,
    });
    if (!systemRes.error) setSystems(Array.isArray(systemRes.data) ? systemRes.data : []);
    if (!wireRes.error) setWireOptions(Array.isArray(wireRes.data) ? wireRes.data : []);
    if (!settingsRes.error) setSettings(settingsRes.data || null);
    if (!clinicSettingsRes.error && clinicSettingsRes.data) {
      setClinicForPdf(await clinicSettingsWithLogo(clinicSettingsRes.data, activeClinic));
    } else {
      setClinicForPdf(activeClinic || null);
    }
  }, [patientId, activeClinic, tutorialDemoData]);

  useEffect(() => { reload(); }, [reload]);

  async function handleResume(row) {
    const caseId = row?.case?.id;
    if (!caseId) return;
    const { error: resumeError } = await supabase.rpc("resume_ortho_case", { p_case_id: caseId });
    if (resumeError) {
      toast.error(resumeError.message || "Could not resume Ortho case.");
      return;
    }
    toast.success("Ortho case resumed.");
    reload();
  }

  const totals = useMemo(() => {
    return rows.reduce((acc, row) => {
      acc.visits += orthoVisitsDoneCount(row);
      acc.remaining += orthoOpenBalanceCentavos(row);
      acc.collected += Number(row.balance?.total_collected_centavos || 0);
      return acc;
    }, { visits: 0, remaining: 0, collected: 0 });
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="ortho-panel" data-tutorial-id="ortho-panel">
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.16em] uppercase text-teal-700">
              <Smile className="w-4 h-4" />
              Ortho Panel
            </div>
            <h2 className="mt-2 text-2xl font-semibold text-slate-950">Orthodontic cases</h2>
            <p className="mt-1 text-sm text-slate-500">
              Ortho cases, billing, and visit history for this patient.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={reload}
              className="h-10 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              <RefreshCw className="inline w-4 h-4 mr-1.5" />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => {
                if (activeCaseBlockMessage) {
                  toast.error(activeCaseBlockMessage);
                  return;
                }
                if (planCreationBlock) {
                  toast.error(planCreationBlock.message);
                  return;
                }
                setImportOpen(true);
              }}
              disabled={!!creationBlockMessage}
              title={creationBlockMessage || undefined}
              data-tutorial-id="ortho-import-existing-case-button"
              className="h-10 px-4 rounded-lg border border-teal-200 bg-white text-teal-700 text-sm font-semibold hover:bg-teal-50 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:bg-slate-100"
            >
              Import Existing Case
            </button>
            <button
              type="button"
              onClick={() => {
                if (activeCaseBlockMessage) {
                  toast.error(activeCaseBlockMessage);
                  return;
                }
                if (planCreationBlock) {
                  toast.error(planCreationBlock.message);
                  return;
                }
                setStartOpen(true);
              }}
              disabled={!!creationBlockMessage}
              title={creationBlockMessage || undefined}
              className="h-10 px-4 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:bg-slate-200 disabled:text-slate-500 disabled:hover:bg-slate-200"
            >
              Start Ortho Case
            </button>
          </div>
        </div>

        {activeCaseBlockMessage ? (
          <div
            className="mt-4 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm font-medium text-teal-900"
            data-testid="ortho-active-case-lock"
          >
            {activeCaseBlockMessage}
          </div>
        ) : null}

        {planCreationBlock ? (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <strong>Missing patient profile field:</strong>
            <span className="ml-1">{planCreationBlock.message}</span>
          </div>
        ) : null}

        {startDoctorProfileBlock ? (
          <div
            role="alert"
            data-testid="ortho-start-doctor-profile-warning"
            className="mt-4 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-900"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
            <div className="min-w-0">
              <p className="text-sm font-semibold">Attending dentist profile incomplete</p>
              <p className="mt-0.5 text-sm">{startDoctorProfileBlock.message}</p>
              {defaultStartDoctor ? (
                <p className="mt-1 text-xs text-rose-700">
                  Default attending dentist: {doctorDisplayName(defaultStartDoctor, user)}. You can open Start Ortho Case and choose another dentist with a complete profile.
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="mt-6">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            All ortho cases
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Metric label="Adjustments done" value={totals.visits} />
            <Metric label="Collected" value={money(totals.collected)} />
            <Metric label="Remaining" value={money(totals.remaining)} />
          </div>
        </div>

        <div className="mt-5 inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
          <button
            type="button"
            onClick={() => setPanelView("cases")}
            className={`h-8 rounded-lg px-3 text-sm font-semibold ${panelView === "cases" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-white"}`}
          >
            Cases
          </button>
          <button
            type="button"
            onClick={() => setPanelView("activity")}
            className={`h-8 rounded-lg px-3 text-sm font-semibold ${panelView === "activity" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-white"}`}
          >
            Activity
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 px-2">Loading ortho cases...</div>
      ) : error ? (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-sm text-rose-700">{error}</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-teal-50 mx-auto flex items-center justify-center">
            <Smile className="w-5 h-5 text-teal-600" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-slate-900">No ortho cases yet.</h3>
          <p className="mt-1 text-sm text-slate-500">Start from chairside or create the case here with a primary doctor selected.</p>
        </div>
      ) : panelView === "activity" ? (
        <OrthoActivityTimeline rows={rows} doctors={doctors} />
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <OrthoCaseCard
              key={row.case.id}
              row={row}
              doctors={doctors}
              user={user}
              patient={patient}
              clinic={clinicForPdf || activeClinic}
              onSetup={() => setSetupCase(row)}
              onRecordVisit={(visitType) => setVisitCase({ row, visitType })}
              onPause={() => setPauseCase(row)}
              onResume={() => handleResume(row)}
              onReconcile={() => setReconcileCase(row)}
              onSetupRetainer={() => setRetainerSetupCase(row)}
              onCancelCase={() => setCancelCase(row)}
              onCancelDebonding={() => setCancelDebondingCase(row)}
              onCollectPayment={() => setPaymentOnlyCase(row)}
              appointmentId={appointmentId}
              onReload={reload}
            />
          ))}
        </div>
      )}

      {startOpen ? (
        <StartOrthoCaseDialog
          patientId={patientId}
          patient={patient}
          doctors={doctors}
          systems={systems}
          clinicRole={clinicRole}
          user={user}
          settings={settings}
          initialPatientDoctorDefaults={patientDoctorDefaults}
          activeBranchId={activeBranchId}
          onClose={() => setStartOpen(false)}
          onSaved={(next) => {
            setStartOpen(false);
            if (next?.openBilling && next?.row) setSetupCase(next.row);
            reload();
          }}
        />
      ) : null}

      {importOpen ? (
        <ImportExistingOrthoCaseDialog
          key={`ortho-transfer-${tutorialStage || "live"}`}
          patientId={patientId}
          doctors={doctors}
          systems={systems}
          activeBranchId={activeBranchId}
          tutorialMode={!!tutorialDemoData}
          tutorialStage={tutorialStage}
          onClose={() => setImportOpen(false)}
          onSaved={(result) => {
            setImportOpen(false);
            if (result?.tutorial) {
              setRows(Array.isArray(tutorialDemoData?.importedRows) ? tutorialDemoData.importedRows : []);
              return;
            }
            reload();
          }}
        />
      ) : null}

      {setupCase ? (
        <SetupBillingDialog
          row={setupCase}
          patient={patient}
          doctors={doctors}
          user={user}
          settings={settings}
          clinic={clinicForPdf || activeClinic}
          onClose={() => setSetupCase(null)}
          onSaved={() => { setSetupCase(null); reload(); }}
        />
      ) : null}

      {visitCase ? (
        <OrthoVisitDialog
          row={visitCase.row}
          visitType={visitCase.visitType}
          appointmentId={appointmentId}
          wireOptions={wireOptions}
          onClose={() => setVisitCase(null)}
          onSaved={() => {
            const savedRow = visitCase.row;
            const savedType = visitCase.visitType;
            setVisitCase(null);
            reload();
            if (savedType === "debonding") {
              setReconcileCase(savedRow);
              return;
            }
            navigate(`/checkout?appointment_id=${appointmentId}`);
          }}
        />
      ) : null}

      {pauseCase ? (
        <PauseCaseDialog
          row={pauseCase}
          onClose={() => setPauseCase(null)}
          onSaved={() => {
            setPauseCase(null);
            reload();
          }}
        />
      ) : null}

      {reconcileCase ? (
        <ReconcileCaseDialog
          row={reconcileCase}
          settings={settings}
          onClose={() => setReconcileCase(null)}
          onSaved={(result) => {
            const closedRow = reconcileCase;
            setReconcileCase(null);
            reload();
            if (result?.completed && closedRow) setRetainerSetupCase(closedRow);
          }}
        />
      ) : null}

      {retainerSetupCase ? (
        <RetainerSetupDialog
          row={retainerSetupCase}
          appointmentId={appointmentId}
          settings={settings}
          onClose={() => setRetainerSetupCase(null)}
          onSaved={() => {
            setRetainerSetupCase(null);
            reload();
            if (appointmentId) navigate(`/checkout?appointment_id=${appointmentId}`);
          }}
        />
      ) : null}

      {cancelCase ? (
        <CancelOrthoCaseDialog
          row={cancelCase}
          settings={settings}
          onClose={() => setCancelCase(null)}
          onSaved={() => {
            setCancelCase(null);
            reload();
          }}
        />
      ) : null}

      {cancelDebondingCase ? (
        <CancelDebondingDialog
          row={cancelDebondingCase}
          onClose={() => setCancelDebondingCase(null)}
          onSaved={() => {
            setCancelDebondingCase(null);
            reload();
          }}
        />
      ) : null}

      {paymentOnlyCase ? (
        <OrthoPaymentOnlyDialog
          row={paymentOnlyCase}
          patient={patient}
          onClose={() => setPaymentOnlyCase(null)}
          onSaved={() => {
            setPaymentOnlyCase(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-semibold text-slate-950">{value}</p>
    </div>
  );
}

function OrthoCaseCard({ row, doctors, user, patient, clinic, onSetup, onRecordVisit, onPause, onResume, onReconcile, onSetupRetainer, onCancelCase, onCancelDebonding, onCollectPayment, appointmentId, onReload }) {
  const [contractDetailsOpen, setContractDetailsOpen] = useState(false);
  const [contractBusy, setContractBusy] = useState(false);
  const [signPromptOpen, setSignPromptOpen] = useState(false);
  const c = row.case || {};
  const b = row.billing;
  const visits = row.visits || [];
  const timelineVisits = Array.isArray(row.timeline) && row.timeline.length ? row.timeline : visits;
  const visitsDone = orthoVisitsDoneCount(row);
  const doctor = doctors.find((d) => d.id === c.primary_doctor_id);
  const doctorProfile = getDoctorProfileForContract(doctor);
  const contractGuardian = {
    guardian_full_name: b?.guardian_full_name,
    guardian_relationship: b?.guardian_relationship,
  };
  const adjustmentVisitLimit = c.estimated_total_visits || b?.estimated_total_visits;
  const progressText = adjustmentVisitLimit ? `${visitsDone} of ${adjustmentVisitLimit}` : `${visitsDone} adjustments`;
  const isPendingSetup = c.status === "active_pending_billing_setup" || c.status === "consultation_pending_acceptance" || !b;
  const systemName = c.system_name || c.system || "Ortho case";
  const photoCount = Array.isArray(c.starting_photos) ? c.starting_photos.length : 0;
  const isClosed = CLOSED_ORTHO_CASE_STATUSES.has(c.status);
  const contractGenerated = !!b?.contract_generated_at;
  const contractSigned = !!b?.contract_signed_at;
  const awaitingSignature = !isPendingSetup && !isClosed && c.status === "active_pending_bonding" && !contractSigned;
  const isMigrated = c.migration_source === "migrated";
  // Transfer cases arrive already bonded at another clinic, so the first visit
  // at our clinic is an adjustment, not a bonding.
  const nextVisitType = !b || isClosed
    ? null
    : (visits.length === 0 && !isMigrated)
      ? "bonding"
      : (c.ready_for_debonding ? "debonding" : "adjustment");
  const canRecordVisit = !!appointmentId && !!nextVisitType && ["active_pending_bonding", "active", "delinquent"].includes(c.status);
  const canPause = ["active", "delinquent"].includes(c.status);
  const canResume = c.status === "paused";
  const canReconcile = !!b && !isClosed && !["consultation_pending_acceptance", "active_pending_billing_setup", "active_pending_bonding"].includes(c.status);
  const canCancelCase = !isClosed && isActiveOrthoCaseStatus(c.status);
  const openArCentavos = Number(row.balance?.ar_outstanding_centavos || 0);
  const monthlySchedule = row.monthly_schedule;
  const isTutorialCase = String(c.id || "").startsWith("tutorial-");
  const cardTutorialId = isTutorialCase ? (isMigrated ? "ortho-transfer-case-card" : "ortho-doctor-case-card") : undefined;
  const metricsTutorialId = isTutorialCase ? (isMigrated ? "ortho-transfer-metrics" : "ortho-doctor-metrics") : undefined;
  const recordVisitTutorialId = isTutorialCase ? (isMigrated ? "ortho-transfer-record-next-visit" : "ortho-doctor-record-adjustment") : undefined;

  async function generateContract(guardian = contractGuardian) {
    if (contractBusy) return;
    const block = getOrthoContractBlock({ patient, doctor, guardian });
    if (block) {
      if (block.code === "missing_guardian_full_name" || block.code === "missing_guardian_relationship") {
        setContractDetailsOpen(true);
        toast.error(block.message);
        return;
      }
      toast.error(block.message);
      return;
    }
    setContractBusy(true);
    try {
      const { data, error } = await supabase.rpc("update_ortho_contract_guardian_details", {
        p_case_id: c.id,
        p_input: {
          guardian_full_name: guardian?.fullName || guardian?.guardian_full_name || null,
          guardian_relationship: guardian?.relationship || guardian?.guardian_relationship || null,
        },
      });
      if (error) throw error;
      const billingSnapshot = data || b;
      const doc = await buildOrthoContractPdf({
        contract: { ...row, billing: billingSnapshot },
        patient,
        clinic,
        doctorName: doctorProfile.fullName,
        doctorPrcLicense: doctorProfile.prcLicense,
      });
      doc.save(pdfFilename(patient, "ortho_contract"));
      toast.success("Ortho contract downloaded.");
      setContractDetailsOpen(false);
      onReload?.();
      if (!b?.contract_signed_at) {
        setSignPromptOpen(true);
      }
    } catch (err) {
      toast.error(err?.message || "Could not generate Ortho contract.");
    } finally {
      setContractBusy(false);
    }
  }

  async function markContractSigned() {
    if (contractBusy) return;
    if (!b?.contract_generated_at) {
      toast.error("Generate the Ortho contract PDF before marking it signed.");
      return;
    }
    const block = getOrthoContractBlock({ patient, doctor, guardian: contractGuardian });
    if (block) {
      if (block.code === "missing_guardian_full_name" || block.code === "missing_guardian_relationship") {
        setContractDetailsOpen(true);
        toast.error(block.message);
        return;
      }
      toast.error(block.message);
      return;
    }
    setContractBusy(true);
    const { error } = await supabase.rpc("mark_ortho_contract_signed", { p_case_id: c.id });
    setContractBusy(false);
    if (error) {
      toast.error(error.message || "Could not mark Ortho contract signed.");
      return;
    }
    toast.success("Signed Ortho contract recorded.");
    onReload?.();
  }

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
      data-tutorial-id={cardTutorialId}
    >
      {c.ready_for_debonding ? (
        <div className="border-b border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-950">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-[0.08em]">
                <Flag className="h-4 w-4" />
                Ready for debonding
              </p>
              <p className="mt-0.5 text-xs text-amber-800">
                Flagged {formatActivityDate(c.debonding_flagged_at)}{flaggedVisitLabel(row) ? ` at ${flaggedVisitLabel(row)}` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={onCancelDebonding}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-amber-200 bg-white px-3 text-xs font-semibold text-amber-800 hover:bg-amber-100"
            >
              <Undo2 className="h-3.5 w-3.5" />
              Cancel debonding schedule
            </button>
          </div>
        </div>
      ) : null}
      <div className="p-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-semibold text-slate-950">{labelize(systemName)}</span>
            {isMigrated ? <TransferCaseBadge /> : null}
            <StatusBadge status={c.status} />
            <PhotoBadge count={photoCount} />
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {labelize(c.case_type || "fixed")} · {doctor ? doctorDisplayName(doctor, user) : "Primary doctor pending"} · {progressText}
            {b?.billing_model ? ` · ${billingModelLabel(b.billing_model)}` : ""}
          </p>
          {adjustmentVisitLimit && !isPendingSetup ? (
            <OrthoVisitProgressBar visitsDone={visitsDone} totalVisits={adjustmentVisitLimit} isClosed={isClosed} />
          ) : null}
          {b?.billing_model === "monthly_recurring" && monthlySchedule ? (
            <p className="mt-1 text-xs font-medium text-slate-600">
              Monthly: {money(monthlySchedule.amount_centavos)} due {formatActivityDate(monthlySchedule.next_due_date)}
              {monthlySchedule.schedule_status ? ` · ${monthlySchedule.schedule_status}` : ""}
            </p>
          ) : null}
          {c.chief_complaint ? <p className="mt-2 text-sm text-slate-700">Chief complaint: {c.chief_complaint}</p> : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canCancelCase ? (
            <button
              type="button"
              onClick={onCancelCase}
              className="h-9 px-3 rounded-lg border border-rose-200 bg-white text-sm font-semibold text-rose-700 hover:bg-rose-50"
            >
              <Ban className="inline h-4 w-4 mr-1.5" />
              Cancel case
            </button>
          ) : null}
          {isPendingSetup ? (
            <button
              type="button"
              onClick={onSetup}
              className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            >
              Setup billing
            </button>
          ) : (
            <>
              {canPause ? (
                <button
                  type="button"
                  onClick={onPause}
                  className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Pause
                </button>
              ) : null}
              {canResume ? (
                <button
                  type="button"
                  onClick={onResume}
                  className="h-9 px-3 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700"
                >
                  Resume
                </button>
              ) : null}
              {canReconcile ? (
                <button
                  type="button"
                  onClick={onReconcile}
                  className="h-9 px-3 rounded-lg border border-amber-200 bg-amber-50 text-sm font-semibold text-amber-800 hover:bg-amber-100"
                >
                  Reconcile
                </button>
              ) : null}
              {isClosed ? null : (
                <button
                  type="button"
                  onClick={() => {
                    const visitBlock = getOrthoVisitStartBlock({ row, visitType: nextVisitType });
                    if (visitBlock) {
                      toast.error(visitBlock.message);
                      return;
                    }
                    onRecordVisit(nextVisitType);
                  }}
                  disabled={!canRecordVisit}
                  title={!appointmentId ? "Open this patient from Doctor View to record an ortho visit." : undefined}
                  data-tutorial-id={recordVisitTutorialId}
                  className="h-9 px-3 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:bg-slate-200 disabled:text-slate-500"
                >
                  Record {labelize(nextVisitType || "visit")}
                </button>
              )}
              <button
                type="button"
                onClick={() => generateContract()}
                disabled={contractBusy}
                data-tutorial-id={isTutorialCase && !isMigrated ? "ortho-doctor-contract" : undefined}
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Download className="inline w-4 h-4 mr-1.5" />
                Contract
              </button>
              {b?.contract_signed_at ? (
                <span className="h-9 inline-flex items-center rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-sm font-semibold text-emerald-700">
                  Signed
                </span>
              ) : (
                <button
                  type="button"
                  onClick={markContractSigned}
                  disabled={contractBusy}
                  title="Record that the patient signed the printed contract. Bonding unlocks after this."
                  className={contractGenerated
                    ? "h-9 px-3 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                    : "h-9 px-3 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"}
                >
                  Mark signed
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {awaitingSignature ? (
        <div className="border-t border-emerald-100 bg-emerald-50/60 px-5 py-2.5 text-xs font-medium text-emerald-900">
          {contractGenerated
            ? "Contract downloaded. Mark it signed once the patient signs the printed copy — bonding unlocks after that."
            : "Next: download the contract, then mark it signed to unlock bonding."}
        </div>
      ) : null}
      <div className="border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5" data-tutorial-id={metricsTutorialId}>
        <MiniStat icon={WalletCards} label="Package price" value={b ? money(b.package_price_centavos) : "Pending"} />
        <MiniStat icon={WalletCards} label={isMigrated ? "Historical import" : "Down payment"} value={isMigrated ? migratedImportText(row) : (b ? downPaymentText(b) : "Pending")} />
        <MiniStat icon={Stethoscope} label="Per payment" value={b ? money(b.per_visit_charge_centavos) : "Pending"} />
        <MiniStat icon={WalletCards} label="Collected" value={money(row.balance?.total_collected_centavos || 0)} />
        <MiniStat icon={CalendarClock} label="Next visit" value={nextVisitText(row)} />
      </div>
      {openArCentavos > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-amber-100 bg-amber-50/70 px-5 py-3">
          <div>
            <p className="text-sm font-semibold text-amber-950">Charged Ortho balance: {money(openArCentavos)}</p>
            <p className="mt-0.5 text-xs text-amber-800">A payment here updates the financial ledger only. It does not create or count an adjustment.</p>
          </div>
          <button
            type="button"
            onClick={onCollectPayment}
            data-testid={`ortho-record-balance-payment-${c.id}`}
            className="h-8 rounded-lg border border-amber-200 bg-white px-3 text-xs font-semibold text-amber-900 hover:bg-amber-100"
          >
            Record payment only
          </button>
        </div>
      ) : null}
      {c.status === "completed" ? (
        <RetainerStatusPanel setup={row.retainer_setup} onSetup={onSetupRetainer} />
      ) : null}
      {b?.billing_model === "milestone" ? (
        <MilestoneStatusPanel milestones={row.milestones || []} />
      ) : null}
      <div data-tutorial-id={isTutorialCase && !isMigrated ? "ortho-doctor-visit-history" : undefined}>
        <VisitTimeline visits={timelineVisits} doctors={doctors} onReload={onReload} patient={patient} />
      </div>
      {contractDetailsOpen ? (
        <GuardianContractDetailsDialog
          patient={patient}
          doctor={doctor}
          billing={b}
          submitting={contractBusy}
          onClose={() => setContractDetailsOpen(false)}
          onSave={(guardian) => generateContract(guardian)}
        />
      ) : null}
      {signPromptOpen ? (
        <MarkSignedPromptDialog
          busy={contractBusy}
          onConfirm={async () => {
            setSignPromptOpen(false);
            await markContractSigned();
          }}
          onClose={() => setSignPromptOpen(false)}
        />
      ) : null}
    </div>
  );
}

function OrthoPaymentOnlyDialog({ row, patient, onClose, onSaved }) {
  const caseId = row?.case?.id;
  const [paymentState, setPaymentState] = useState(null);
  const [amountPesos, setAmountPesos] = useState("");
  const [method, setMethod] = useState("cash");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [paidByName, setPaidByName] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const idempotencyKeyRef = useRef("");

  useEffect(() => {
    let active = true;
    idempotencyKeyRef.current = globalThis.crypto?.randomUUID?.()
      || `ortho-payment-only-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setLoading(true);
    setLoadError("");
    supabase.rpc("get_ortho_case_payment_state", { p_case_id: caseId })
      .then(({ data, error: rpcError }) => {
        if (!active) return;
        setLoading(false);
        if (rpcError) {
          setLoadError(rpcError.message || "Could not load the Ortho balance.");
          return;
        }
        const next = data || {};
        setPaymentState(next);
        setAmountPesos(String(Number(next.open_ar_centavos || 0) / 100));
      });
    return () => { active = false; };
  }, [caseId]);

  const amountCentavos = pesosToCentavos(amountPesos);
  const openArCentavos = Number(paymentState?.open_ar_centavos || 0);
  const referenceRequired = method !== "cash";
  const canSubmit = !loading
    && !loadError
    && !submitting
    && amountCentavos > 0
    && amountCentavos <= openArCentavos
    && (!referenceRequired || referenceNumber.trim().length > 0);

  async function submit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const { error: rpcError } = await supabase.rpc("collect_ortho_outstanding_payment", {
      p_case_id: caseId,
      p_amount_centavos: amountCentavos,
      p_method: method,
      p_reference_number: referenceNumber.trim() || null,
      p_notes: notes.trim() || null,
      p_paid_by_name: paidByName.trim() || null,
      p_idempotency_key: idempotencyKeyRef.current,
    });
    setSubmitting(false);
    if (rpcError) {
      toast.error(rpcError.message || "Could not record the Ortho payment.");
      return;
    }
    toast.success(`Recorded ${money(amountCentavos)} toward the Ortho balance. No adjustment was created.`);
    onSaved?.();
  }

  return (
    <Modal title="Record Ortho balance payment" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="ortho-payment-only-dialog">
        <div className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-950">
          <p className="font-semibold">Payment only — no clinical visit</p>
          <p className="mt-1 text-xs leading-5 text-teal-800">
            This records money against {patient?.full_name || "the patient"}&apos;s already charged Ortho balance. It will not create an adjustment, change wires, or post production credit.
          </p>
        </div>

        {loading ? <p className="text-sm text-slate-500">Loading charged balance...</p> : null}
        {loadError ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{loadError}</p> : null}

        {!loading && !loadError && openArCentavos <= 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <p className="font-semibold">No charged Ortho balance to collect.</p>
            <p className="mt-1 text-xs text-amber-800">Future adjustment payments cannot be prepaid here. Use this only for installments already posted to AR.</p>
          </div>
        ) : null}

        {!loading && !loadError && paymentState ? (
          <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <PaymentOnlyMetric label="Package paid" value={money(paymentState.total_collected_centavos)} />
              <PaymentOnlyMetric label="Charged balance" value={money(openArCentavos)} />
              <PaymentOnlyMetric label="Package remaining" value={money(paymentState.contract_remaining_centavos)} />
            </div>

            {openArCentavos > 0 ? (
              <>
                <Field label="Amount to collect">
                  <div className="flex rounded-lg border border-slate-200 focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-100">
                    <span className="inline-flex h-10 items-center border-r border-slate-100 bg-slate-50 px-3 text-sm text-slate-500">₱</span>
                    <input
                      data-testid="ortho-payment-only-amount"
                      type="number"
                      min="0"
                      max={openArCentavos / 100}
                      step="0.01"
                      value={amountPesos}
                      onChange={(event) => setAmountPesos(event.target.value)}
                      className="h-10 min-w-0 flex-1 rounded-r-lg px-3 text-sm text-slate-900 outline-none"
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Maximum: {money(openArCentavos)} of already-posted Ortho AR.</p>
                </Field>

                <Field label="Payment method">
                  <select value={method} onChange={(event) => setMethod(event.target.value)} className={INPUT_CLASS}>
                    <option value="cash">Cash</option>
                    <option value="gcash">GCash</option>
                    <option value="maya">Maya</option>
                    <option value="bank_transfer">Bank transfer</option>
                    <option value="card">Card</option>
                    <option value="check">Check</option>
                  </select>
                </Field>

                {referenceRequired ? (
                  <Field label="Reference number">
                    <input
                      data-testid="ortho-payment-only-reference"
                      value={referenceNumber}
                      onChange={(event) => setReferenceNumber(event.target.value)}
                      placeholder="Required for non-cash payment"
                      className={INPUT_CLASS}
                    />
                  </Field>
                ) : null}

                <Field label="Paid by (optional)">
                  <input
                    data-testid="ortho-payment-only-paid-by"
                    value={paidByName}
                    onChange={(event) => setPaidByName(event.target.value)}
                    placeholder="e.g. Patient's mother"
                    className={INPUT_CLASS}
                  />
                </Field>

                <Field label="Note (optional)">
                  <textarea
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="e.g. Catch-up payment for missed installments"
                    className="min-h-20 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                  />
                </Field>

                <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-600">Total paid after this payment</span>
                    <span className="font-semibold text-slate-950">{money(Number(paymentState.total_collected_centavos || 0) + amountCentavos)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-3">
                    <span className="text-slate-600">Remaining balance after this payment</span>
                    <span className="font-semibold text-slate-950">{money(Math.max(Number(paymentState.contract_remaining_centavos || 0) - amountCentavos, 0))}</span>
                  </div>
                </div>
              </>
            ) : null}
          </>
        ) : null}

        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={submitting} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            Cancel
          </button>
          {openArCentavos > 0 ? (
            <button data-testid="ortho-payment-only-submit" type="submit" disabled={!canSubmit} className="h-10 rounded-lg bg-teal-600 px-4 text-sm font-semibold text-white hover:bg-teal-700 disabled:bg-slate-200 disabled:text-slate-500">
              {submitting ? "Recording..." : "Record payment only"}
            </button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}

function PaymentOnlyMetric({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-semibold text-slate-950">{value}</p>
    </div>
  );
}

function MarkSignedPromptDialog({ busy, onConfirm, onClose }) {
  return (
    <Modal title="Mark contract signed?" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm leading-6 text-slate-600">
          The contract PDF downloaded. Print it for the patient to sign. Once they've signed,
          record it here — bonding stays locked until a signed contract is on file.
        </p>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="h-10 px-4 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Not yet
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
          >
            Patient signed — mark signed
          </button>
        </div>
      </div>
    </Modal>
  );
}

function GuardianContractDetailsDialog({ patient, doctor, billing, submitting, onClose, onSave }) {
  const [form, setForm] = useState({
    guardian_full_name: billing?.guardian_full_name || "",
    guardian_relationship: billing?.guardian_relationship || "",
  });
  const minor = isPatientMinor(patient);

  function submit(e) {
    e.preventDefault();
    const block = getOrthoContractBlock({ patient, doctor, guardian: form });
    if (block) {
      toast.error(block.message);
      return;
    }
    onSave?.(form);
  }

  return (
    <Modal title="Contract Details" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {minor ? (
          <>
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              This patient is under 18 today. Guardian full name and relationship are required for the Ortho contract.
            </div>
            <Field label="Guardian full name">
              <input
                className={INPUT_CLASS}
                value={form.guardian_full_name}
                onChange={(e) => setForm((f) => ({ ...f, guardian_full_name: e.target.value }))}
                placeholder="Full legal name"
              />
            </Field>
            <Field label="Guardian relationship to patient">
              <input
                className={INPUT_CLASS}
                value={form.guardian_relationship}
                onChange={(e) => setForm((f) => ({ ...f, guardian_relationship: e.target.value }))}
                placeholder="e.g. Mother, Father, Legal guardian"
              />
            </Field>
          </>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            This patient is 18 or older today. Guardian fields are hidden on the contract.
          </div>
        )}
        <DialogActions submitting={submitting} submitLabel="Generate contract" onClose={onClose} />
      </form>
    </Modal>
  );
}

function MilestoneStatusPanel({ milestones }) {
  return (
    <div className="border-t border-slate-100 px-5 py-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Milestone billing</p>
        <span className="text-xs text-slate-500">{milestones.length} milestone{milestones.length === 1 ? "" : "s"}</span>
      </div>
      {milestones.length === 0 ? (
        <p className="text-sm text-slate-500">No remaining milestones configured.</p>
      ) : (
        <div className="space-y-2">
          {milestones.map((milestone) => (
            <div key={milestone.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
              <div>
                <p className="font-semibold text-slate-800">{milestone.due_condition || "Milestone"}</p>
                <p className="text-xs text-slate-500">
                  {milestone.due_date ? `Due ${formatActivityDate(milestone.due_date)}` : milestone.trigger_event ? `Triggered by ${labelize(milestone.trigger_event)}` : "Manual trigger"}
                </p>
              </div>
              <div className="text-right">
                <p className="font-semibold tabular-nums text-slate-900">{money(milestone.amount_centavos)}</p>
                <p className={milestone.display_status === "Overdue" ? "text-xs font-semibold text-rose-700" : "text-xs font-medium text-slate-500"}>{milestone.display_status || labelize(milestone.status)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RetainerStatusPanel({ setup, onSetup }) {
  const configured = !!setup?.id;
  const typeLine = configured
    ? `Upper ${retainerTypeLabel(setup.upper_retainer_type, setup.upper_retainer_other_description)} · Lower ${retainerTypeLabel(setup.lower_retainer_type, setup.lower_retainer_other_description)}`
    : "Retainer not configured";
  const impressionLine = configured
    ? retainerDateLine("Impression", setup.impression_status, setup.impression_scheduled_at)
    : "Impression: Not configured";
  const installationLine = configured
    ? retainerDateLine("Installation", setup.installation_status, setup.installation_scheduled_at)
    : "Installation: Not configured";

  return (
    <div className="border-t border-slate-100 px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 text-sm text-slate-700">
          <p>{typeLine}</p>
          <p>{impressionLine}</p>
          <p>{installationLine}</p>
        </div>
        {!configured ? (
          <button
            type="button"
            onClick={onSetup}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 text-sm font-semibold text-teal-800 hover:bg-teal-100"
          >
            <CalendarClock className="h-4 w-4" />
            Set up retainer
          </button>
        ) : null}
      </div>
    </div>
  );
}

function flaggedVisitLabel(row) {
  const flaggedId = row?.case?.debonding_flagged_visit_id;
  if (!flaggedId) return "";
  const match = (row?.visits || []).find((entry) => entry?.ortho_visit?.id === flaggedId);
  const ov = match?.ortho_visit;
  if (!ov?.id) return "";
  return orthoEventLabel(ov);
}

function CancelOrthoCaseDialog({ row, settings, onClose, onSaved }) {
  const defaultRefund = ["none", "full", "partial"].includes(settings?.default_refund_on_cancel)
    ? settings.default_refund_on_cancel
    : "none";
  const [reason, setReason] = useState("");
  const [refundDecision, setRefundDecision] = useState(defaultRefund);
  const [refundNote, setRefundNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const c = row?.case || {};
  const b = row?.billing || null;
  const collected = Number(row?.balance?.total_collected_centavos || 0);
  const hasFinancialRecord = !!b || collected > 0;

  async function submit() {
    if (submitting) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      toast.error("Cancellation reason is required.");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.rpc("cancel_ortho_case", {
      p_case_id: c.id,
      p_reason: trimmedReason,
      p_refund_decision: refundDecision,
      p_refund_note: refundNote.trim() || null,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not cancel Ortho case.");
      return;
    }
    toast.success("Ortho case cancelled.");
    onSaved?.();
  }

  return (
    <Modal title="Cancel Ortho case" onClose={onClose}>
      <div className="space-y-4">
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
          <div className="font-semibold">{labelize(c.system_name || c.system || "Ortho case")}</div>
          <div className="mt-1 text-xs">
            This changes the case status to cancelled and voids the Ortho contract if billing already exists.
          </div>
        </div>

        <Field label="Cancellation reason">
          <textarea
            className={`${INPUT_CLASS} min-h-24 py-2`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. patient changed mind"
            data-testid="ortho-cancel-case-reason"
          />
        </Field>

        {hasFinancialRecord ? (
          <>
            <Field label="Refund decision">
              <Segmented
                value={refundDecision}
                onChange={setRefundDecision}
                options={[
                  { value: "none", label: "No refund" },
                  { value: "full", label: "Full refund" },
                  { value: "partial", label: "Partial refund" },
                ]}
              />
            </Field>
            <Field label="Refund note optional">
              <input
                className={INPUT_CLASS}
                value={refundNote}
                onChange={(e) => setRefundNote(e.target.value)}
                placeholder="e.g. refund handled outside Dental OS"
              />
            </Field>
          </>
        ) : null}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Keep case
          </button>
          <button type="button" onClick={submit} disabled={submitting} className="h-10 px-4 rounded-lg bg-rose-600 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
            Cancel Ortho case
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CancelDebondingDialog({ row, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (submitting) return;
    setSubmitting(true);
    const { error } = await supabase.rpc("unflag_case_for_debonding", {
      p_case_id: row?.case?.id,
      p_reason: reason.trim() || null,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not cancel debonding schedule.");
      return;
    }
    toast.success("Debonding schedule cancelled.");
    onSaved?.();
  }

  return (
    <Modal title="Cancel debonding schedule?" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          This case will return to active adjustment treatment. Any scheduled debonding appointment will be reverted to adjustment.
        </p>
        <Field label="Reason optional">
          <input
            className={INPUT_CLASS}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. continue active treatment"
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={submitting} className="h-10 px-4 rounded-lg bg-teal-600 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50">
            Confirm
          </button>
        </div>
      </div>
    </Modal>
  );
}

function VisitTimeline({ visits, doctors, onReload, patient }) {
  const rows = [...(visits || [])].sort((a, b) => Number(b.ortho_visit?.visit_number || 0) - Number(a.ortho_visit?.visit_number || 0));
  const [photoTarget, setPhotoTarget] = useState(null);
  if (rows.length === 0) {
    return (
      <div className="border-t border-slate-100 px-5 py-4 text-sm text-slate-400">
        No ortho visits recorded yet.
      </div>
    );
  }
  return (
    <div className="border-t border-slate-100 px-5 py-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Visit timeline</div>
      <div className="mt-3 space-y-2">
        {rows.map((entry, index) => {
          const ov = entry.ortho_visit || entry || {};
          const previousOv = rows[index + 1]?.ortho_visit || rows[index + 1] || null;
          const pv = entry.procedure_visit || {};
          const pi = entry.procedure_instance || {};
          const doctor = doctors.find((d) => d.id === (pv.performed_by || entry.performed_by));
          const upperWire = formatWireSelection(wireSelectionFromVisit(ov, "upper", [])) || String(ov.upper_wire_label_snapshot || "").replace(/^Other:\s*/i, "").trim();
          const lowerWire = formatWireSelection(wireSelectionFromVisit(ov, "lower", [])) || String(ov.lower_wire_label_snapshot || "").replace(/^Other:\s*/i, "").trim();
          const previousUpperWire = formatWireSelection(wireSelectionFromVisit(previousOv, "upper", [])) || String(previousOv?.upper_wire_label_snapshot || "").replace(/^Other:\s*/i, "").trim();
          const previousLowerWire = formatWireSelection(wireSelectionFromVisit(previousOv, "lower", [])) || String(previousOv?.lower_wire_label_snapshot || "").replace(/^Other:\s*/i, "").trim();
          const photoList = Array.isArray(ov.photos) ? ov.photos : [];
          const storedPhotoCount = photoList.filter((p) => p?.storage_path).length;
          const legacyPhotoCount = photoList.length - storedPhotoCount;
          const chargeCentavos = entry.charge_centavos ?? entry.charge_event?.amount_centavos ?? pi.committed_fee_centavos ?? 0;
          const collectedCentavos = entry.collected_centavos ?? 0;
          const arCollectedCentavos = entry.ar_collected_centavos ?? 0;
          const paymentStatus = String(entry.payment_status || "").trim();
          const isPartiallyCollected = paymentStatus.toLowerCase() === "partially collected";
          const shortfallCentavos = Math.max(Number(chargeCentavos) - Number(collectedCentavos), 0);
          const occurredAt = entry.occurred_at || pv.completed_at || ov.created_at;
          return (
            <div key={ov.id || entry.event_id || pv.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-semibold text-slate-900">
                  {entry.event_label || orthoEventLabel(ov)}
                </div>
                <div className="text-xs font-semibold text-slate-500">{money(chargeCentavos)}</div>
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {doctor ? doctorDisplayName(doctor) : "Doctor"} · {formatOrthoEventDate(occurredAt)} · OH {labelize(ov.oh_status || "not set")} · {ov.next_visit_interval_weeks || "?"}w next
              </div>
              <div className="mt-1 space-y-0.5 text-xs text-slate-600">
                {upperWire ? <div>Upper wire: {upperWire}{orthoTimelineStatusText(upperWire, previousUpperWire)}</div> : null}
                {lowerWire ? <div>Lower wire: {lowerWire}{orthoTimelineStatusText(lowerWire, previousLowerWire)}</div> : null}
                {ov.elastics_prescribed ? <div>Elastics: {ov.elastics_prescribed}{orthoTimelineStatusText(ov.elastics_prescribed, previousOv?.elastics_prescribed)}</div> : null}
                {ov.notes ? <div>Note: {ov.notes}</div> : null}
              </div>
              {isPartiallyCollected ? (
                <div
                  className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs"
                  data-testid="ortho-partial-payment-alert"
                >
                  <div className="flex flex-wrap items-center justify-between gap-1.5 font-semibold text-amber-900">
                    <span>Partially collected</span>
                    <span>{money(shortfallCentavos)} shortfall</span>
                  </div>
                  <div className="mt-0.5 text-amber-800">
                    Collected {money(collectedCentavos)} of {money(chargeCentavos)}{Number(arCollectedCentavos) > 0 ? ` · ${money(arCollectedCentavos)} later collected through AR` : ""}
                  </div>
                </div>
              ) : Number(chargeCentavos) > 0 ? (
                <div className="mt-1 text-xs text-slate-600">
                  Charge {money(chargeCentavos)} · Collected {money(collectedCentavos)}{Number(arCollectedCentavos) > 0 ? ` (AR ${money(arCollectedCentavos)})` : ""}{paymentStatus ? ` · ${paymentStatus}` : ""}
                </div>
              ) : ov.non_adjustment_reason ? (
                <div className="mt-1 text-xs text-slate-600">Reason: {labelize(ov.non_adjustment_reason)} · No Ortho installment</div>
              ) : null}
              <div className="mt-2"><OrthoVisitPhotos photos={ov.photos} /></div>
              <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                {storedPhotoCount > 0 ? (
                  <span className="text-slate-500">{storedPhotoCount} photo{storedPhotoCount === 1 ? "" : "s"}</span>
                ) : legacyPhotoCount > 0 ? (
                  <span className="italic text-slate-400">{legacyPhotoCount} photo{legacyPhotoCount === 1 ? "" : "s"} · no image saved</span>
                ) : (
                  <span className="font-medium text-amber-700">No photos</span>
                )}
                {ov.id ? (
                  <button
                    type="button"
                    onClick={() => setPhotoTarget(entry)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    <Camera className="h-3.5 w-3.5" />
                    Add photos
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      {photoTarget ? (
        <OrthoVisitPhotoUploader
          entry={photoTarget}
          patient={patient}
          onClose={() => setPhotoTarget(null)}
          onSaved={() => { setPhotoTarget(null); onReload?.(); }}
        />
      ) : null}
    </div>
  );
}

function OrthoVisitPhotoUploader({ entry, patient, onClose, onSaved }) {
  const { activeClinicId } = useAuth();
  const ov = entry.ortho_visit || entry || {};
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const names = files.map((file) => file.name);

  function handlePicked(e) {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  }

  async function handleSave() {
    if (submitting || files.length === 0) return;
    const patientId = patient?.id;
    if (!patientId || !activeClinicId) {
      toast.error("Missing patient or clinic — reopen this patient's record and try again.");
      return;
    }
    setSubmitting(true);
    const stage = orthoStageForVisitType(ov.visit_type);
    let photos = [];
    try {
      const uploads = await Promise.all(files.map((file) => uploadPatientFile({
        patientId,
        clinicId: activeClinicId,
        file,
        category: "ortho_case_photos",
        orthoCaseId: ov.case_id,
        orthoStage: stage,
      })));
      photos = uploads.map((res, i) => ({
        name: files[i]?.name || res?.original_filename || "photo",
        storage_path: res?.storage_path || null,
        file_id: res?.id || null,
        stage,
        captured_at: new Date().toISOString(),
        source: "staff_upload_ortho_panel",
      }));
    } catch (uploadErr) {
      setSubmitting(false);
      toast.error(uploadErr?.message || "Could not upload photos. Try again.");
      return;
    }
    const { error } = await supabase.rpc("append_ortho_visit_photos", {
      p_visit_id: ov.id,
      p_photos: photos,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not attach photos.");
      return;
    }
    toast.success(`Added ${files.length} photo${files.length === 1 ? "" : "s"}.`);
    onSaved?.();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-950">Add photos to {orthoEventLabel(ov)}</h3>
        <p className="mt-1 text-sm text-slate-600">{labelize(ov.visit_type || "visit")}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-teal-600 px-3 text-sm font-semibold text-white hover:bg-teal-700"
          >
            <Camera className="h-4 w-4" />
            Take photo
          </button>
          <button
            type="button"
            onClick={() => uploadInputRef.current?.click()}
            className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Upload files
          </button>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handlePicked} className="sr-only" />
          <input ref={uploadInputRef} type="file" multiple accept="image/*" onChange={handlePicked} className="sr-only" />
        </div>
        {names.length ? (
          <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-2 text-xs text-slate-600">
            <p className="mb-1 font-semibold text-slate-800">{names.length} selected</p>
            <p className="break-words">{names.join(", ")}</p>
          </div>
        ) : (
          <p className="mt-3 text-xs text-slate-500">Pick at least one photo to attach.</p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-9 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting || names.length === 0}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-teal-600 px-3 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
          >
            {submitting ? "Saving..." : "Save photos"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrthoActivityTimeline({ rows, doctors }) {
  const activities = useMemo(() => buildOrthoActivityRows(rows, doctors), [rows, doctors]);
  const activityCounts = useMemo(() => {
    return activities.reduce((acc, item) => {
      acc.total += 1;
      if (item.tone === "visit") acc.visits += 1;
      if (item.tone === "payment") acc.payments += 1;
      return acc;
    }, { total: 0, visits: 0, payments: 0 });
  }, [activities]);

  if (!activities.length) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 mx-auto flex items-center justify-center">
          <Activity className="w-5 h-5 text-slate-500" />
        </div>
        <h3 className="mt-4 text-base font-semibold text-slate-900">No ortho activity yet.</h3>
        <p className="mt-1 text-sm text-slate-500">Visits, billing setup, and payments will appear here newest first.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/40">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              <Activity className="w-4 h-4" />
              Ortho activity
            </div>
            <p className="mt-1 text-sm text-slate-500">Newest first. Visits, payments, and case events are separated so the history is readable.</p>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1">{activityCounts.total} events</span>
            <span className="rounded-full border border-teal-100 bg-teal-50 px-2.5 py-1 text-teal-700">{activityCounts.visits} visits</span>
            <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-emerald-700">{activityCounts.payments} payments</span>
          </div>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {activities.map((item) => (
          <div key={item.id} className="px-5 py-4 hover:bg-slate-50/50">
            <div className="grid grid-cols-[36px_minmax(0,1fr)_auto] gap-3">
              <span className={`mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full ${activityToneClasses(item.tone).icon}`}>
                {item.tone === "payment" || item.tone === "historical" ? <CreditCard className="w-4 h-4" /> : <CalendarClock className="w-4 h-4" />}
              </span>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-slate-950">{item.title}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${activityToneClasses(item.tone).badge}`}>
                    {activityBadgeLabel(item)}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{item.subtitle}</p>
                {item.meta?.length ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {item.meta.map((meta) => (
                      <span key={`${item.id}-${meta.label}`} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600">
                        <span className="font-semibold text-slate-400">{meta.label}</span>
                        <span className="font-medium text-slate-700">{meta.value}</span>
                      </span>
                    ))}
                  </div>
                ) : null}
                {item.note ? (
                  <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs italic text-slate-600">
                    "{item.note}"
                  </p>
                ) : null}
              </div>

              <div className="text-right whitespace-nowrap">
                <p className="text-xs font-medium text-slate-400">{formatActivityDate(item.date)}</p>
                {item.amountCentavos != null ? (
                  <p className={`mt-1 text-sm font-semibold ${item.tone === "payment" ? "text-emerald-700" : "text-slate-800"}`}>
                    {item.tone === "payment" ? "+" : ""}{money(item.amountCentavos)}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function activityBadgeLabel(item) {
  if (item?.tone === "case") return "Event";
  return item?.kindLabel || "Activity";
}

function buildOrthoActivityRows(rows, doctors) {
  const activities = [];
  (rows || []).forEach((row) => {
    const c = row.case || {};
    const b = row.billing || null;
    const systemName = labelize(c.system_name || c.system || "Ortho case");
    const doctor = doctors.find((d) => d.id === c.primary_doctor_id);

    if (c.created_at) {
      activities.push({
        id: `case-${c.id}`,
        date: c.created_at,
        tone: "case",
        kindLabel: "Case",
        title: "Case started",
        subtitle: `${systemName} · ${doctor ? doctorDisplayName(doctor) : "Primary doctor pending"}`,
        meta: c.chief_complaint ? [{ label: "Complaint", value: c.chief_complaint }] : [],
      });
    }

    if (b?.created_at) {
      activities.push({
        id: `billing-${b.id || c.id}`,
        date: b.created_at,
        tone: "case",
        kindLabel: "Billing",
        title: "Billing setup saved",
        subtitle: systemName,
        meta: [
          { label: "Package", value: money(b.package_price_centavos || 0) },
          { label: "Down payment", value: money(b.down_payment_centavos || 0) },
          { label: "Per payment", value: money(b.per_visit_charge_centavos || 0) },
        ],
      });
    }

    if (c.debonding_flagged_at) {
      activities.push({
        id: `debonding-flag-${c.id}`,
        date: c.debonding_flagged_at,
        tone: "case",
        kindLabel: "Debond",
        title: "Scheduled for debonding",
        subtitle: systemName,
        meta: [
          { label: "Timing", value: c.debonding_timing === "schedule_sooner" ? "Sooner appointment" : "Next regular visit" },
          flaggedVisitLabel(row) ? { label: "Flagged at", value: flaggedVisitLabel(row) } : null,
        ].filter(Boolean),
      });
    }

    if (c.debonding_unflagged_at) {
      activities.push({
        id: `debonding-unflag-${c.id}`,
        date: c.debonding_unflagged_at,
        tone: "case",
        kindLabel: "Debond",
        title: "Debonding schedule cancelled",
        subtitle: systemName,
        meta: [],
        note: c.debonding_unflag_reason || null,
      });
    }

    (row.visits || []).forEach((entry) => {
      const ov = entry.ortho_visit || {};
      const pv = entry.procedure_visit || {};
      const pi = entry.procedure_instance || {};
      const visitDoctor = doctors.find((d) => d.id === pv.performed_by);
      const upperWire = formatWireSelection(wireSelectionFromVisit(ov, "upper", [])) || String(ov.upper_wire_label_snapshot || "").replace(/^Other:\s*/i, "").trim();
      const lowerWire = formatWireSelection(wireSelectionFromVisit(ov, "lower", [])) || String(ov.lower_wire_label_snapshot || "").replace(/^Other:\s*/i, "").trim();
      activities.push({
        id: `visit-${ov.id || pv.id}`,
        date: ov.created_at || pv.completed_at || pv.created_at,
        tone: "visit",
        kindLabel: "Visit",
        title: orthoEventLabel(ov),
        subtitle: systemName,
        meta: [
          { label: "Doctor", value: visitDoctor ? doctorDisplayName(visitDoctor) : "Doctor" },
          { label: "OH", value: labelize(ov.oh_status || "not set") },
          ov.next_visit_interval_weeks ? { label: "Next", value: `${ov.next_visit_interval_weeks}w` } : null,
          upperWire ? { label: "Upper wire", value: upperWire } : null,
          lowerWire ? { label: "Lower wire", value: lowerWire } : null,
          ov.elastics_prescribed ? { label: "Elastics", value: ov.elastics_prescribed } : null,
          Array.isArray(ov.photos) && ov.photos.length ? { label: "Photos", value: `${ov.photos.length}` } : null,
        ].filter(Boolean),
        note: ov.notes || null,
        amountCentavos: Number(pi.committed_fee_centavos || 0),
      });
    });

    (row.payments || []).forEach((payment) => {
      const amount = Number(payment.allocated_centavos || payment.amount_centavos || 0);
      const isPaymentOnly = payment.metadata?.source === "ortho_payment_only_collection";
      activities.push({
        id: `payment-${payment.id}`,
        date: payment.occurred_at || payment.created_at || payment.event_date,
        tone: "payment",
        kindLabel: "Payment",
        title: isPaymentOnly ? "Payment-only collection" : "Payment received",
        subtitle: systemName,
        meta: [
          { label: "Method", value: paymentMethodLabel(payment.payment_method) },
          payment.metadata?.paid_by_name ? { label: "Paid by", value: payment.metadata.paid_by_name } : null,
          isPaymentOnly ? { label: "Clinical", value: "No visit created" } : null,
          paymentReceipt(payment) ? { label: "Receipt", value: paymentReceipt(payment) } : null,
          amount !== Number(payment.amount_centavos || 0) ? { label: "Allocated", value: `${money(amount)} of ${money(payment.amount_centavos || 0)}` } : { label: "Allocated", value: "This case" },
        ].filter(Boolean),
        note: payment.metadata?.notes || null,
        amountCentavos: amount,
      });
    });

    (row.reconciliations || []).forEach((rec) => {
      activities.push({
        id: `reconciliation-${rec.id}`,
        date: rec.decided_at || rec.created_at,
        tone: "case",
        kindLabel: "Close",
        title: "Case reconciled",
        subtitle: systemName,
        meta: [
          { label: "Decision", value: labelize(rec.resolution_type || "reconciled") },
          Number(rec.amount_centavos || 0) > 0 ? { label: "Amount", value: money(rec.amount_centavos) } : null,
          rec.approved_by ? { label: "Approval", value: "Approved" } : null,
        ].filter(Boolean),
        note: rec.notes || null,
        amountCentavos: Number(rec.amount_centavos || 0) || null,
      });
    });

    (row.overpayment_decisions || []).forEach((decision) => {
      activities.push({
        id: `overpayment-${decision.id}`,
        date: decision.decided_at || decision.created_at,
        tone: "payment",
        kindLabel: "Overpayment",
        title: "Overpayment handled",
        subtitle: systemName,
        meta: [{ label: "Decision", value: labelize(decision.decision || "recorded") }],
        note: decision.notes || null,
        amountCentavos: Number(decision.amount_centavos || 0),
      });
    });

    (row.historical_payments || []).forEach((payment) => {
      activities.push({
        id: `historical-payment-${payment.id}`,
        date: payment.recorded_at || payment.created_at,
        tone: "historical",
        kindLabel: "Imported",
        title: "Imported historical payment",
        subtitle: `${systemName} · migration note only`,
        meta: [{ label: "Ledger", value: "Not counted" }],
        note: payment.notes || null,
        amountCentavos: Number(payment.total_paid_prior_centavos || 0),
      });
    });
  });

  return activities
    .filter((item) => item.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

function activityToneClasses(tone) {
  if (tone === "payment") {
    return {
      icon: "bg-emerald-50 text-emerald-700",
      badge: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    };
  }
  if (tone === "historical") {
    return {
      icon: "bg-amber-50 text-amber-700",
      badge: "bg-amber-50 text-amber-700 border border-amber-100",
    };
  }
  if (tone === "case") {
    return {
      icon: "bg-slate-100 text-slate-600",
      badge: "bg-slate-100 text-slate-600 border border-slate-200",
    };
  }
  return {
    icon: "bg-teal-50 text-teal-700",
    badge: "bg-teal-50 text-teal-700 border border-teal-100",
  };
}

function PhotoBadge({ count }) {
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
      <Camera className="w-3 h-3" />
      {count} photo{count === 1 ? "" : "s"}
    </span>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="px-5 py-4 border-t sm:border-t-0 sm:border-l first:border-l-0 border-slate-100">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-400">
        <Icon className="w-4 h-4" />
        {label}
      </p>
      <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}

export function StartOrthoCaseDialog({ patientId, patient, doctors, systems, clinicRole, user, settings, initialPatientDoctorDefaults = null, activeBranchId = "", onClose, onSaved }) {
  const [submitting, setSubmitting] = useState(false);
  const [patientDoctorDefaults, setPatientDoctorDefaults] = useState({
    loaded: false,
    owningDentistId: null,
    preferredDoctorId: null,
  });
  const [doctorTouched, setDoctorTouched] = useState(false);
  const [changingDoctor, setChangingDoctor] = useState(false);
  const [visitMode, setVisitMode] = useState("preset");
  const activeSystems = useMemo(() => {
    const allowed = SYSTEM_CATEGORIES_BY_CASE.fixed;
    return systems.filter((system) => allowed.includes(system.category));
  }, [systems]);
  const [form, setForm] = useState({
    case_type: "fixed",
    system_id: "",
    estimated_total_visits: 24,
    primary_doctor_id: "",
    chief_complaint: "",
    notes: "",
  });
  const caseSystems = useMemo(() => {
    const allowed = SYSTEM_CATEGORIES_BY_CASE[form.case_type] || [];
    return systems.filter((system) => allowed.includes(system.category));
  }, [form.case_type, systems]);
  const selectedDoctor = doctors.find((d) => d.id === form.primary_doctor_id);
  const selectedDoctorProfileBlock = selectedDoctor ? getOrthoDoctorProfileBlock(selectedDoctor) : null;
  const selectedSystem = systems.find((system) => system.id === form.system_id);
  const canSetupBilling = canCompleteOrthoBilling(settings, clinicRole);
  const patientName = String(
    patient?.full_name
      || [patient?.first_name, patient?.last_name].filter(Boolean).join(" ")
      || "Patient",
  ).trim();
  const patientInitials = patientName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "P";
  const patientAge = calculateAgeFromDob(patient?.date_of_birth);
  const caseTypeLabel = CASE_TYPES.find((option) => option.value === form.case_type)?.label || labelize(form.case_type);
  const systemLabel = selectedSystem?.display_name || "Select system";
  const doctorLabel = selectedDoctor ? doctorDisplayName(selectedDoctor, user) : "Select doctor";
  const nextStepLabel = canSetupBilling
    ? "next: set up billing and print the contract."
    : "next: front desk completes billing setup.";

  useEffect(() => {
    let cancelled = false;
    if (initialPatientDoctorDefaults?.loaded) {
      setPatientDoctorDefaults(initialPatientDoctorDefaults);
      return () => { cancelled = true; };
    }
    setPatientDoctorDefaults({ loaded: false, owningDentistId: null, preferredDoctorId: null });
    if (!patientId) return () => { cancelled = true; };
    (async () => {
      const { data, error } = await supabase
        .from("patients")
        .select("owning_dentist_id, preferred_doctor_id")
        .eq("id", patientId)
        .maybeSingle();
      if (cancelled) return;
      setPatientDoctorDefaults({
        loaded: true,
        owningDentistId: error ? null : data?.owning_dentist_id ?? null,
        preferredDoctorId: error ? null : data?.preferred_doctor_id ?? null,
      });
    })();
    return () => { cancelled = true; };
  }, [initialPatientDoctorDefaults, patientId]);

  useEffect(() => {
    const defaultDoctor = resolveOrthoPrimaryDoctor({
      doctors,
      clinicRole,
      userId: user?.id,
      patientDoctorDefaults,
    });
    if (!doctorTouched && defaultDoctor?.id && form.primary_doctor_id !== defaultDoctor.id) {
      setForm((f) => ({ ...f, primary_doctor_id: defaultDoctor.id }));
    }
  }, [clinicRole, doctorTouched, doctors, form.primary_doctor_id, patientDoctorDefaults, user?.id]);

  useEffect(() => {
    const selectedStillValid = caseSystems.some((system) => system.id === form.system_id);
    if (!selectedStillValid && caseSystems[0]?.id) {
      setForm((f) => ({ ...f, system_id: caseSystems[0].id }));
    }
  }, [caseSystems, form.system_id]);

  useEffect(() => {
    if (form.primary_doctor_id) return;
    const firstDoctor = doctors.find((doctor) => hasReadableDoctorName(doctor)) || doctors[0];
    if (firstDoctor?.id) setForm((current) => ({ ...current, primary_doctor_id: firstDoctor.id }));
  }, [doctors, form.primary_doctor_id]);

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    const patientBlock = getOrthoPlanCreationBlock({ patient });
    if (patientBlock) { toast.error(patientBlock.message); return; }
    if (!selectedDoctor) { toast.error("Select a valid primary doctor."); return; }
    if (!selectedSystem) { toast.error("Select an Ortho system."); return; }
    if (!String(form.chief_complaint).trim()) { toast.error("Chief complaint is required."); return; }
    setSubmitting(true);
    const { data, error } = await supabase.rpc("start_ortho_case", {
      p_patient_id: patientId,
      p_input: {
        case_type: form.case_type,
        system_id: form.system_id,
        estimated_total_visits: Number(form.estimated_total_visits || 0),
        primary_doctor_id: form.primary_doctor_id,
        branch_id: activeBranchId || undefined,
        chief_complaint: form.chief_complaint.trim(),
        notes: form.notes.trim() || null,
      },
    });
    setSubmitting(false);
    if (error) { toast.error(error.message || "Could not start Ortho case."); return; }
    if (canSetupBilling) {
      toast.success("Ortho case started. Set up billing next.");
      onSaved({
        openBilling: true,
        row: {
          case: {
            ...(data || {}),
            system_name: selectedSystem.display_name,
            system_default_price_centavos: selectedSystem.default_price_centavos,
            chief_complaint: form.chief_complaint.trim(),
            primary_doctor_name: doctorDisplayName(selectedDoctor, user),
          },
          billing: null,
          visits: [],
          balance: {},
        },
      });
    } else {
      toast.success("Ortho case saved for front desk billing setup.");
      onSaved();
    }
  }

  return (
    <Modal title="Start Ortho Case" onClose={onClose} contentClassName="max-h-[80vh] overflow-y-auto p-0">
      <form onSubmit={submit} data-testid="ortho-start-case-form">
        <div className="space-y-5 p-5">
        <div
          data-testid="ortho-start-patient-context"
          className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-100 text-sm font-semibold text-teal-700">
              {patientInitials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-950">
                {patientName}
                {patientAge != null ? <span className="font-medium text-slate-500"> · {patientAge} y</span> : null}
              </p>
              <p className="text-xs text-slate-500">New ortho case</p>
            </div>
          </div>
          <span
            data-testid="ortho-start-medical-history-status"
            className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
              patient?.has_medical_history
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-rose-200 bg-rose-50 text-rose-700"
            }`}
          >
            {patient?.has_medical_history ? "Medical history on file" : "Medical history not on file"}
          </span>
        </div>
        <Field label="Case type">
          <Segmented grouped value={form.case_type} options={CASE_TYPES} onChange={(case_type) => setForm((f) => ({ ...f, case_type, system_id: "" }))} />
        </Field>
        <Field label="System">
          <select className={INPUT_CLASS} value={form.system_id} onChange={(e) => setForm((f) => ({ ...f, system_id: e.target.value }))}>
            <option value="">Select system</option>
            {(caseSystems.length ? caseSystems : activeSystems).map((system) => (
              <option key={system.id} value={system.id}>{system.display_name}</option>
            ))}
          </select>
          {selectedSystem?.default_price_centavos ? (
            <p className="mt-1 text-xs text-slate-500">Default package price: {money(selectedSystem.default_price_centavos)}. Staff can edit this during billing setup.</p>
          ) : (
            <p className="mt-1 text-xs text-slate-500">Systems are managed in Settings → Ortho → Systems.</p>
          )}
        </Field>
        <Field label="Estimated adjustment visits">
          <div className="flex flex-wrap gap-2">
            {VISIT_PICKERS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => { setVisitMode("preset"); setForm((f) => ({ ...f, estimated_total_visits: n })); }}
                className={pill(visitMode === "preset" && Number(form.estimated_total_visits) === n)}
              >
                {n}
              </button>
            ))}
            <button type="button" onClick={() => setVisitMode("other")} className={pill(visitMode === "other")}>Other</button>
            {visitMode === "other" ? (
              <input
                type="number"
                min="1"
                className="h-9 w-24 rounded-lg border border-slate-200 px-3 text-sm"
                value={form.estimated_total_visits}
                onChange={(e) => setForm((f) => ({ ...f, estimated_total_visits: e.target.value }))}
              />
            ) : null}
          </div>
        </Field>
        <Field label="Chief complaint">
          <input
            className={INPUT_CLASS}
            value={form.chief_complaint}
            onChange={(e) => setForm((f) => ({ ...f, chief_complaint: e.target.value }))}
            placeholder="e.g. Crowding, spacing, aesthetic-only"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {COMPLAINTS.map((label) => (
              <button key={label} type="button" onClick={() => setForm((f) => ({ ...f, chief_complaint: label }))} className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50">{label}</button>
            ))}
          </div>
        </Field>
        <Field label="Primary doctor">
          {!changingDoctor && selectedDoctor ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-teal-100 bg-teal-50 px-3 py-2">
              <div>
                <p className="text-sm font-semibold text-teal-950">{doctorDisplayName(selectedDoctor, user)}</p>
                <p className="text-xs text-teal-700">Auto-assigned as primary doctor.</p>
              </div>
              <button type="button" onClick={() => setChangingDoctor(true)} className="text-xs font-semibold text-teal-700 hover:text-teal-900">Change</button>
            </div>
          ) : (
            <select
              className={INPUT_CLASS}
              value={form.primary_doctor_id}
              onChange={(e) => {
                setDoctorTouched(true);
                setChangingDoctor(false);
                setForm((f) => ({ ...f, primary_doctor_id: e.target.value }));
              }}
            >
              <option value="">Select doctor</option>
              {doctors.map((d) => <option key={d.id} value={d.id}>{doctorDisplayName(d, user)}</option>)}
            </select>
          )}
          {selectedDoctorProfileBlock ? (
            <div
              role="alert"
              data-testid="ortho-selected-doctor-profile-warning"
              className="mt-2 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-600" />
              <span>{selectedDoctorProfileBlock.message}</span>
            </div>
          ) : null}
        </Field>
        <Field label="Notes for front desk">
          <textarea
            className="min-h-[76px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Optional handoff note, e.g. patient asked about monthly terms."
          />
          <p className="mt-1 text-xs text-slate-500">Starting photos are required at bonding. Consultation photos can be added later from the case files.</p>
        </Field>
        <div
          data-testid="ortho-start-summary"
          className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-4 text-teal-950"
        >
          <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-700">
            <ClipboardCheck className="h-4 w-4" />
            Starting
          </p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <p className="text-xs text-teal-700">Case</p>
              <p className="text-base font-semibold">{caseTypeLabel} · {systemLabel}</p>
            </div>
            <div>
              <p className="text-xs text-teal-700">Doctor</p>
              <p className="text-base font-semibold">{doctorLabel}</p>
            </div>
          </div>
          <p className="mt-2 text-xs text-teal-700">
            ~{Number(form.estimated_total_visits || 0)} adjustments · installation is Day 0 · {nextStepLabel}
          </p>
        </div>
        </div>
        <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-slate-100 bg-white px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-11 rounded-lg border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="h-11 rounded-lg bg-teal-600 px-5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
          >
            {submitting ? "Saving..." : canSetupBilling ? "Save & setup billing" : "Save & hand off to front desk"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ImportExistingOrthoCaseDialog({ patientId, doctors, systems, activeBranchId = "", tutorialMode = false, tutorialStage = "", onClose, onSaved }) {
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(ORTHO_TRANSFER_DETAIL_STAGES.has(tutorialStage) ? "details" : "billing");
  const [form, setForm] = useState(() => initialOrthoImportForm(doctors, tutorialStage));
  const caseSystems = useMemo(() => {
    const allowed = SYSTEM_CATEGORIES_BY_CASE[form.case_type] || [];
    return systems.filter((system) => allowed.includes(system.category));
  }, [form.case_type, systems]);
  const selectedDoctor = doctors.find((d) => d.id === form.primary_doctor_id)
    || (tutorialMode ? doctors.find((doctor) => hasReadableDoctorName(doctor)) || doctors[0] : undefined);
  const selectedSystem = systems.find((system) => system.id === form.system_id)
    || (tutorialMode ? caseSystems[0] : undefined);
  const selectedBillingModel = ORTHO_BILLING_MODELS.find((model) => model.value === form.billing_model) || ORTHO_BILLING_MODELS[0];
  const packageModel = ["per_visit_divisor", "monthly_recurring", "milestone"].includes(form.billing_model);
  const showPackageFields = packageModel && form.payment_history_known;
  const showPaidPrior = form.payment_history_known && form.billing_model !== "flat_per_visit";
  const showRemainingVisits = form.billing_model === "per_visit_divisor";
  const showPerVisit = ["per_visit_divisor", "flat_per_visit"].includes(form.billing_model);

  useEffect(() => {
    const selectedStillValid = caseSystems.some((system) => system.id === form.system_id);
    if (!selectedStillValid && caseSystems[0]?.id) {
      setForm((f) => ({ ...f, system_id: caseSystems[0].id }));
    }
  }, [caseSystems, form.system_id]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    const packageCents = pesosToCentavos(form.package_price);
    const paidPriorCents = pesosToCentavos(form.total_paid_prior);
    const estimatedRemainingCents = pesosToCentavos(form.estimated_remaining_balance);
    const perVisitCents = form.per_visit_charge ? pesosToCentavos(form.per_visit_charge) : 0;
    const monthlyAmountCents = form.monthly_amount ? pesosToCentavos(form.monthly_amount) : 0;
    const milestoneAmountCents = form.milestone_amount ? pesosToCentavos(form.milestone_amount) : 0;
    const remainingVisits = Number(form.estimated_remaining_visits || 0);
    const approximateVisitCount = form.approximate_visit_count === "" ? null : Number(form.approximate_visit_count);
    const monthlyDueDay = form.monthly_due_day === "" ? null : Number(form.monthly_due_day);

    if (!selectedDoctor) { toast.error("Primary doctor is required for existing Ortho cases."); return; }
    if (!selectedSystem) { toast.error("Ortho system is required."); return; }
    if (!form.migration_started_at) { toast.error("Original start date is required."); return; }
    if (paidPriorCents < 0 || perVisitCents < 0 || packageCents < 0 || monthlyAmountCents < 0 || milestoneAmountCents < 0 || estimatedRemainingCents < 0) {
      toast.error("Money amounts cannot be negative.");
      return;
    }
    if (showPackageFields && form.original_price_known && packageCents <= 0) { toast.error("Package price must be greater than zero."); return; }
    if (showPackageFields && !form.original_price_known && estimatedRemainingCents < 0) { toast.error("Estimated remaining balance is required."); return; }
    if (showRemainingVisits && (!Number.isFinite(remainingVisits) || remainingVisits < 1)) { toast.error("Remaining visits must be at least 1."); return; }
    if (form.billing_model === "flat_per_visit" && perVisitCents <= 0) { toast.error("Flat per-visit cases require a fee per visit."); return; }
    if (form.billing_model === "monthly_recurring") {
      if (monthlyAmountCents <= 0) { toast.error("Monthly cases require a monthly amount."); return; }
      if (!Number.isFinite(monthlyDueDay) || monthlyDueDay < 1 || monthlyDueDay > 31) { toast.error("Monthly due day must be 1 to 31."); return; }
      if (!form.next_monthly_due_date) { toast.error("Monthly cases require the next due date."); return; }
    }
    if (form.billing_model === "milestone") {
      if (milestoneAmountCents <= 0) { toast.error("Milestone cases require at least one milestone amount."); return; }
      if (!form.milestone_due_condition.trim()) { toast.error("Milestone cases require a due condition."); return; }
    }
    if (form.billing_model === "lump_sum" && paidPriorCents <= 0) { toast.error("Lump-sum cases require total paid."); return; }
    if (approximateVisitCount != null && (!Number.isFinite(approximateVisitCount) || approximateVisitCount < 0)) {
      toast.error("Approximate visits already done cannot be negative.");
      return;
    }

    if (tutorialMode) {
      toast.success("Tutorial import complete. No clinic case was saved.");
      onSaved?.({ tutorial: true });
      return;
    }

    setSubmitting(true);
    const input = compactObject({
      billing_model: form.billing_model,
      case_type: form.case_type,
      system_id: selectedSystem.id,
      system: selectedSystem.display_name || selectedSystem.name || selectedSystem.label,
      primary_doctor_id: form.primary_doctor_id,
      branch_id: activeBranchId || undefined,
      original_doctor_unknown: !!form.original_doctor_unknown,
      migration_started_at: form.migration_started_at,
      status: form.status,
      original_price_known: !!form.original_price_known,
      payment_history_known: !!form.payment_history_known,
      migration_payment_status: form.payment_history_known ? form.migration_payment_status : "unknown",
      migration_payment_notes: form.migration_payment_notes.trim() || undefined,
      package_price_centavos: showPackageFields && form.original_price_known ? packageCents : undefined,
      estimated_remaining_balance_centavos: showPackageFields && !form.original_price_known ? estimatedRemainingCents : undefined,
      total_paid_prior_centavos: showPaidPrior ? paidPriorCents : undefined,
      estimated_remaining_visits: showRemainingVisits ? remainingVisits : undefined,
      per_visit_charge_centavos: showPerVisit && form.per_visit_charge ? perVisitCents : undefined,
      monthly_amount_centavos: form.billing_model === "monthly_recurring" ? monthlyAmountCents : undefined,
      monthly_due_day: form.billing_model === "monthly_recurring" ? monthlyDueDay : undefined,
      next_monthly_due_date: form.billing_model === "monthly_recurring" ? form.next_monthly_due_date : undefined,
      milestones: form.billing_model === "milestone" ? [{
        amount_centavos: milestoneAmountCents,
        due_condition: form.milestone_due_condition.trim(),
        due_date: form.milestone_due_date || undefined,
      }] : undefined,
      approximate_visit_count: approximateVisitCount,
      last_visit_date: form.last_visit_date || undefined,
      migration_data_quality: form.payment_history_known ? form.migration_data_quality : "minimal",
      migration_notes: form.migration_notes.trim() || undefined,
      historical_payment_notes: form.historical_payment_notes.trim() || undefined,
      historical_visit_notes: form.historical_visit_notes.trim() || undefined,
      notes: form.notes.trim() || undefined,
    });

    const { error } = await supabase.rpc("import_ortho_case", {
      p_patient_id: patientId,
      p_input: input,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not import existing Ortho case.");
      return;
    }
    toast.success("Existing Ortho case imported.");
    onSaved?.();
  }

  return (
    <Modal title="Import Existing Ortho Case" onClose={onClose} tutorialId="ortho-import-dialog">
      {step === "billing" ? (
        <div className="space-y-4">
          <Field label="How is this case being billed?">
            <div className="space-y-2" data-tutorial-id="ortho-import-billing-models">
              {ORTHO_BILLING_MODELS.map((model) => (
                <button
                  key={model.value}
                  type="button"
                  onClick={() => setField("billing_model", model.value)}
                  data-tutorial-id={BILLING_MODEL_TUTORIAL_IDS[model.value]}
                  className={`w-full rounded-xl border px-3 py-3 text-left ${form.billing_model === model.value ? "border-teal-400 bg-teal-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                >
                  <span className="flex items-start gap-2">
                    <span className={`mt-0.5 h-4 w-4 rounded-full border ${form.billing_model === model.value ? "border-teal-600 bg-teal-600 shadow-[inset_0_0_0_3px_white]" : "border-slate-300"}`} />
                    <span>
                      <span className="block text-sm font-semibold text-slate-950">{model.label}</span>
                      <span className="block text-xs text-slate-500">{model.desc}</span>
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="h-9 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button type="button" onClick={() => setStep("details")} data-tutorial-id="ortho-import-next" className="h-9 rounded-lg bg-teal-600 px-4 text-sm font-semibold text-white hover:bg-teal-700">
              Next
            </button>
          </div>
        </div>
      ) : (
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-tutorial-id="ortho-import-context-notice">
          {selectedBillingModel.label}: imported payments are historical context only. They do not create new Dental OS revenue or production credit.
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Case type">
            <Segmented tutorialId="ortho-import-case-type" tutorialIdsByValue={CASE_TYPE_TUTORIAL_IDS} value={form.case_type} options={CASE_TYPES} onChange={(case_type) => setForm((f) => ({ ...f, case_type, system_id: "" }))} />
          </Field>
          <Field label="Ortho system">
            <select className={INPUT_CLASS} data-tutorial-id="ortho-import-system" value={form.system_id} onChange={(e) => setField("system_id", e.target.value)}>
              <option value="">Select system</option>
              {caseSystems.map((system) => (
                <option key={system.id} value={system.id}>{system.display_name}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Primary doctor">
            <select className={INPUT_CLASS} data-tutorial-id="ortho-import-primary-doctor" value={form.primary_doctor_id} onChange={(e) => setField("primary_doctor_id", e.target.value)}>
              <option value="">Select doctor</option>
              {doctors.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>{doctorDisplayName(doctor)}</option>
              ))}
            </select>
            <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                data-tutorial-id="ortho-import-original-doctor-unknown"
                checked={!!form.original_doctor_unknown}
                onChange={(e) => setField("original_doctor_unknown", e.target.checked)}
              />
              Original doctor unknown; this is the going-forward doctor
            </label>
          </Field>
          <Field label="Original start date">
            <input
              type="date"
              className={INPUT_CLASS}
              data-testid="ortho-import-started-at"
              data-tutorial-id="ortho-import-started-at"
              value={form.migration_started_at}
              onChange={(e) => {
                setField("migration_started_at", e.target.value);
                emitTutorialFieldChanged(e);
              }}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Case status">
            <select className={INPUT_CLASS} data-tutorial-id="ortho-import-status" value={form.status} onChange={(e) => setField("status", e.target.value)}>
              <option value="active">Active treatment</option>
              <option value="delinquent">Already overdue</option>
              <option value="completed">Already completed</option>
            </select>
          </Field>
          <Field label="Payment status">
            <select className={INPUT_CLASS} data-tutorial-id="ortho-import-payment-status" value={form.migration_payment_status} onChange={(e) => setField("migration_payment_status", e.target.value)}>
              <option value="current">Current</option>
              <option value="already_overdue">Already overdue</option>
              <option value="credit_balance">Credit balance / overpaid</option>
              <option value="settled">Settled</option>
              <option value="unknown">Unknown</option>
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
            <input
              type="checkbox"
              data-tutorial-id="ortho-import-original-price-unknown"
              checked={!form.original_price_known}
              onChange={(e) => setField("original_price_known", !e.target.checked)}
            />
            Original price unknown
          </label>
          <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
            <input
              type="checkbox"
              data-tutorial-id="ortho-import-payment-history-unknown"
              checked={!form.payment_history_known}
              onChange={(e) => {
                const unknown = e.target.checked;
                setForm((prev) => ({
                  ...prev,
                  payment_history_known: !unknown,
                  migration_data_quality: unknown ? "minimal" : prev.migration_data_quality,
                  migration_payment_status: unknown ? "unknown" : prev.migration_payment_status,
                }));
              }}
            />
            Payment history unknown
          </label>
        </div>

        {showPackageFields || showPaidPrior ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {showPackageFields ? (
              form.original_price_known ? (
                <Field label="Package price">
                  <MoneyInput value={form.package_price} onChange={(package_price) => setField("package_price", package_price)} inputProps={{ "data-testid": "ortho-import-package-price", "data-tutorial-id": "ortho-import-package-price" }} />
                </Field>
              ) : (
                <Field label="Estimated remaining balance">
                  <MoneyInput value={form.estimated_remaining_balance} onChange={(estimated_remaining_balance) => setField("estimated_remaining_balance", estimated_remaining_balance)} inputProps={{ "data-testid": "ortho-import-estimated-remaining", "data-tutorial-id": "ortho-import-estimated-remaining" }} />
                </Field>
              )
            ) : null}
            {showPaidPrior ? (
              <Field label={form.billing_model === "lump_sum" ? "Total paid" : "Paid before Dental OS"}>
                <MoneyInput value={form.total_paid_prior} onChange={(total_paid_prior) => setField("total_paid_prior", total_paid_prior)} inputProps={{ "data-testid": "ortho-import-paid-prior", "data-tutorial-id": "ortho-import-paid-prior" }} />
              </Field>
            ) : null}
          </div>
        ) : null}

        {showRemainingVisits || showPerVisit ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {showRemainingVisits ? (
              <Field label="Remaining visits">
                <input
                  type="number"
                  min="1"
                  className={INPUT_CLASS}
                  data-testid="ortho-import-remaining-visits"
                  data-tutorial-id="ortho-import-remaining-visits"
                  value={form.estimated_remaining_visits}
                  onChange={(e) => {
                    setField("estimated_remaining_visits", e.target.value);
                    emitTutorialFieldChanged(e);
                  }}
                />
              </Field>
            ) : null}
            {showPerVisit ? (
              <Field label={form.billing_model === "flat_per_visit" ? "Flat fee per visit" : "Per-visit charge optional"}>
                <MoneyInput value={form.per_visit_charge} onChange={(per_visit_charge) => setField("per_visit_charge", per_visit_charge)} inputProps={{ "data-testid": "ortho-import-per-visit", "data-tutorial-id": "ortho-import-per-visit" }} />
                {form.billing_model === "per_visit_divisor" ? <p className="mt-1 text-xs text-slate-500">Leave blank to calculate from package less prior paid over remaining visits.</p> : null}
              </Field>
            ) : null}
          </div>
        ) : null}

        {form.billing_model === "monthly_recurring" ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Monthly amount">
              <MoneyInput value={form.monthly_amount} onChange={(monthly_amount) => setField("monthly_amount", monthly_amount)} inputProps={{ "data-testid": "ortho-import-monthly-amount", "data-tutorial-id": "ortho-import-monthly-amount" }} />
            </Field>
            <Field label="Due day">
              <input type="number" min="1" max="31" className={INPUT_CLASS} value={form.monthly_due_day} onChange={(e) => setField("monthly_due_day", e.target.value)} />
            </Field>
            <Field label="Next due date">
              <input type="date" className={INPUT_CLASS} value={form.next_monthly_due_date} onChange={(e) => setField("next_monthly_due_date", e.target.value)} />
            </Field>
          </div>
        ) : null}

        {form.billing_model === "milestone" ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Milestone amount">
              <MoneyInput value={form.milestone_amount} onChange={(milestone_amount) => setField("milestone_amount", milestone_amount)} inputProps={{ "data-testid": "ortho-import-milestone-amount", "data-tutorial-id": "ortho-import-milestone-amount" }} />
            </Field>
            <Field label="Due condition">
              <input className={INPUT_CLASS} value={form.milestone_due_condition} onChange={(e) => setField("milestone_due_condition", e.target.value)} placeholder="Month 12 / at debonding" />
            </Field>
            <Field label="Due date optional">
              <input type="date" className={INPUT_CLASS} value={form.milestone_due_date} onChange={(e) => setField("milestone_due_date", e.target.value)} />
            </Field>
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Visits already done">
            <input
              type="number"
              min="0"
              className={INPUT_CLASS}
              data-testid="ortho-import-approximate-visits"
              data-tutorial-id="ortho-import-approximate-visits"
              value={form.approximate_visit_count}
              onChange={(e) => {
                setField("approximate_visit_count", e.target.value);
                emitTutorialFieldChanged(e);
              }}
              placeholder="Approximate"
            />
          </Field>
          <Field label="Last visit date">
            <input
              type="date"
              className={INPUT_CLASS}
              data-testid="ortho-import-last-visit"
              data-tutorial-id="ortho-import-last-visit"
              value={form.last_visit_date}
              onChange={(e) => {
                setField("last_visit_date", e.target.value);
                emitTutorialFieldChanged(e);
              }}
            />
          </Field>
        </div>

        <Field label="Data quality">
          <select className={INPUT_CLASS} data-tutorial-id="ortho-import-data-quality" value={form.migration_data_quality} disabled={!form.payment_history_known} onChange={(e) => setField("migration_data_quality", e.target.value)}>
            <option value="complete">Complete records</option>
            <option value="partial">Partial records</option>
            <option value="minimal">Minimal records</option>
          </select>
        </Field>

        <Field label="Import notes">
          <textarea
            className="min-h-[76px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            data-tutorial-id="ortho-import-notes"
            value={form.migration_notes}
            onChange={(e) => {
              setField("migration_notes", e.target.value);
              emitTutorialFieldChanged(e);
            }}
            placeholder="What was known at import time?"
          />
        </Field>

        <Field label="Payment status note">
          <textarea
            className="min-h-[64px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            data-tutorial-id="ortho-import-payment-status-note"
            value={form.migration_payment_notes}
            onChange={(e) => {
              setField("migration_payment_notes", e.target.value);
              emitTutorialFieldChanged(e);
            }}
            placeholder="Why overdue, why credit balance, or what is unknown."
          />
        </Field>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-tutorial-id="ortho-import-optional-notes">
          <Field label="Payment history note">
            <textarea
              className="min-h-[70px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              data-tutorial-id="ortho-import-payment-history-note"
              value={form.historical_payment_notes}
              onChange={(e) => {
                setField("historical_payment_notes", e.target.value);
                emitTutorialFieldChanged(e);
              }}
              placeholder="e.g. Paid via GCash and cash before migration."
            />
          </Field>
          <Field label="Clinical history note">
            <textarea
              className="min-h-[70px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              data-tutorial-id="ortho-import-clinical-history-note"
              value={form.historical_visit_notes}
              onChange={(e) => {
                setField("historical_visit_notes", e.target.value);
                emitTutorialFieldChanged(e);
              }}
              placeholder="e.g. Wire sequence unknown; patient says seven adjustments done."
            />
          </Field>
        </div>

        <Field label="Case note">
          <textarea
            className="min-h-[70px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            data-tutorial-id="ortho-import-case-note"
            value={form.notes}
            onChange={(e) => {
              setField("notes", e.target.value);
              emitTutorialFieldChanged(e);
            }}
            placeholder="Optional note visible on the case."
          />
        </Field>

        <DialogActions submitting={submitting} submitLabel="Import case" submitTutorialId="ortho-import-submit" onClose={onClose} onBack={() => setStep("billing")} />
      </form>
      )}
    </Modal>
  );
}

function OrthoPlanCard({ termsPreview, previewFits, isFullPayment }) {
  if (!termsPreview) {
    return (
      <div data-testid="ortho-billing-preview" className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center gap-2 text-slate-500">
          <WalletCards className="h-4 w-4" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">The plan</span>
        </div>
        <p className="mt-2 text-sm font-semibold text-slate-700">Payment schedule not calculated yet.</p>
        <p className="mt-1 text-xs text-slate-500">Dental OS calculates and validates the schedule from the database before anything is saved.</p>
      </div>
    );
  }
  if (!previewFits) {
    return (
      <div data-testid="ortho-billing-preview" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-900">
        <div className="flex items-center gap-2 text-rose-700">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">Plan doesn't fit yet</span>
        </div>
        <p className="mt-2 text-sm font-semibold">This payment plan does not fit the current visit estimate.</p>
        <p className="mt-1 text-xs">
          It needs at least {termsPreview.minimum_estimated_total_visits} clinical visits; the current entry is {termsPreview.estimated_total_visits}.
          Increase Estimated clinical visits to {termsPreview.minimum_estimated_total_visits} or raise the preferred monthly payment.
        </p>
      </div>
    );
  }

  const packageCents = Number(termsPreview.package_price_centavos || 0);
  const downCents = Number(termsPreview.down_payment_centavos || 0);
  const perVisit = Number(termsPreview.per_visit_charge_centavos || 0);
  const finalPayment = Number(termsPreview.final_payment_centavos || 0);
  const count = Number(termsPreview.number_of_payments || 0);
  const requiredVisits = Number(termsPreview.required_clinical_visits || 0);
  const hasDown = Number(termsPreview.down_payment_visit_slots || 0) > 0 && downCents > 0;
  const differentFinal = finalPayment > 0 && finalPayment !== perVisit;
  const downPct = packageCents > 0 ? Math.max(4, Math.min(96, Math.round((downCents / packageCents) * 100))) : 0;

  if (isFullPayment) {
    return (
      <div data-testid="ortho-billing-preview" className="rounded-xl border border-teal-100 bg-teal-50 p-4">
        <div className="flex items-center gap-2">
          <WalletCards className="h-4 w-4 text-teal-700" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-teal-700">Full payment</span>
        </div>
        <div className="mt-3 text-2xl font-semibold leading-none text-teal-950">{money(packageCents)}</div>
        <p className="mt-2 text-xs text-teal-700">Due on Visit 1. Future ortho visits are clinical only.</p>
      </div>
    );
  }

  return (
    <div data-testid="ortho-billing-preview" className="rounded-xl border border-teal-100 bg-teal-50 p-4">
      <div className="flex items-center gap-2">
        <WalletCards className="h-4 w-4 text-teal-700" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-teal-700">The plan</span>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-x-5 gap-y-2">
        {hasDown ? (
          <>
            <div>
              <div className="text-[11px] text-teal-700">Visit 1 · down payment</div>
              <div className="text-2xl font-semibold leading-none text-teal-950">{money(downCents)}</div>
            </div>
            <div className="pb-1 text-teal-400">then</div>
          </>
        ) : null}
        <div>
          <div className="text-[11px] text-teal-700">Each visit</div>
          <div className="text-2xl font-semibold leading-none text-teal-950">{money(perVisit)}</div>
        </div>
        <div className="pb-1 text-xs text-teal-600">× {count} payment{count === 1 ? "" : "s"}</div>
      </div>
      {differentFinal ? (
        <div className="mt-2 text-xs font-semibold text-teal-800">Final payment is {money(finalPayment)} so the total never exceeds the package.</div>
      ) : null}
      {hasDown ? (
        <div className="mt-3 flex h-3 overflow-hidden rounded-full">
          <div className="bg-teal-600" style={{ width: `${downPct}%` }} />
          <div className="bg-teal-300" style={{ width: `${100 - downPct}%` }} />
        </div>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-teal-700">
        <span>{requiredVisits} clinical visit{requiredVisits === 1 ? "" : "s"} required</span>
        <span className="font-semibold text-teal-950">{money(packageCents)} total</span>
      </div>
    </div>
  );
}

export function SetupBillingDialog({ row, patient, doctors = [], clinic, onClose, onSaved, embedded = false, onBack, onHandOff, settings }) {
  const c = row.case || {};
  const b = row.billing || null;
  const doctor = doctors.find((d) => d.id === c.primary_doctor_id);
  const doctorProfile = getDoctorProfileForContract(doctor);
  const minorPatient = isPatientMinor(patient);
  const currentAdjustmentVisitEstimate = Math.max(1, Number(c.estimated_total_visits || b?.estimated_total_visits || 24));
  const allowFullPayment = settings?.allow_full_payment_billing !== false;
  const [submitting, setSubmitting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [termsPreview, setTermsPreview] = useState(null);
  const [calculationError, setCalculationError] = useState(null);
  const calculationRequestRef = useRef(0);
  const [handOffNotice, setHandOffNotice] = useState(false);
  const [wizardStep, setWizardStep] = useState("billing");
  const [form, setForm] = useState({
    billing_mode: b?.number_of_payments === 1 && Number(b?.down_payment_centavos || 0) === 0 ? "full_payment" : "payment_plan",
    payment_plan_basis: b?.preferred_payment_centavos ? "preferred_amount" : "payment_count",
    package_price: b?.package_price_centavos ? centavosToPesoInput(b.package_price_centavos) : (c.system_default_price_centavos ? centavosToPesoInput(c.system_default_price_centavos) : ""),
    down_payment: b?.down_payment_centavos ? centavosToPesoInput(b.down_payment_centavos) : "",
    preferred_payment: b?.preferred_payment_centavos ? centavosToPesoInput(b.preferred_payment_centavos) : "",
    number_of_payments: b?.number_of_payments || currentAdjustmentVisitEstimate,
    estimated_total_visits: currentAdjustmentVisitEstimate,
    guardian_full_name: b?.guardian_full_name || "",
    guardian_relationship: b?.guardian_relationship || "",
  });
  const packageCents = pesosToCentavos(form.package_price);
  const isFullPayment = form.billing_mode === "full_payment";
  const dpCents = isFullPayment ? 0 : pesosToCentavos(form.down_payment);
  const preferredPaymentCents = isFullPayment || form.payment_plan_basis !== "preferred_amount"
    ? null
    : pesosToCentavos(form.preferred_payment);
  const previewFits = termsPreview?.fits_clinical_visit_limit === true;
  const previewReady = Boolean(termsPreview && previewFits);
  const showWizard = !embedded && (onBack || onHandOff);
  const canHandOff = showWizard && Boolean(onHandOff);

  const billingTermsInput = useMemo(() => ({
    billing_mode: form.billing_mode,
    billing_variant: isFullPayment ? "C" : "B",
    package_price_centavos: packageCents,
    down_payment_centavos: dpCents,
    estimated_total_visits: Number(form.estimated_total_visits || 0),
    ...(isFullPayment
      ? {}
      : form.payment_plan_basis === "preferred_amount"
        ? { preferred_payment_centavos: preferredPaymentCents }
        : { number_of_payments: Number(form.number_of_payments || 0) }),
  }), [dpCents, form.billing_mode, form.estimated_total_visits, form.number_of_payments, form.payment_plan_basis, isFullPayment, packageCents, preferredPaymentCents]);

  const calculationInputError = !packageCents
    ? "Package price is required."
    : !isFullPayment && form.payment_plan_basis === "preferred_amount" && !preferredPaymentCents
      ? "Enter the patient's preferred monthly payment."
      : !isFullPayment && form.payment_plan_basis === "payment_count" && !Number(form.number_of_payments || 0)
        ? "Number of payments is required."
        : !Number(form.estimated_total_visits || 0)
          ? "Estimated adjustment visits are required."
          : isFullPayment && !allowFullPayment
            ? "Full payment is disabled in Ortho settings."
            : null;

  function patchForm(patch) {
    calculationRequestRef.current += 1;
    setCalculating(false);
    setTermsPreview(null);
    setCalculationError(null);
    setHandOffNotice(false);
    setForm((f) => ({ ...f, ...patch }));
  }

  const calculateTerms = useCallback(async ({ silent = false } = {}) => {
    if (calculationInputError) {
      setCalculating(false);
      if (!silent) toast.error(calculationInputError);
      return null;
    }

    const requestId = calculationRequestRef.current + 1;
    calculationRequestRef.current = requestId;
    setCalculating(true);
    setCalculationError(null);
    const { data, error } = await supabase.rpc("calculate_ortho_billing_terms", {
      p_case_id: c.id,
      p_input: billingTermsInput,
    });
    if (requestId !== calculationRequestRef.current) return null;
    setCalculating(false);

    if (error) {
      const message = error.message || "Could not calculate Ortho payment terms.";
      setCalculationError(message);
      if (!silent) toast.error(message);
      return null;
    }

    setCalculationError(null);
    setTermsPreview(data);
    if (!silent) {
      if (data?.fits_clinical_visit_limit) {
        toast.success("Payment schedule calculated. Review before saving.");
      } else {
        toast.error(`This plan needs at least ${data?.minimum_estimated_total_visits || "more"} adjustment visits. Increase the adjustment estimate or raise the preferred payment.`);
      }
    }
    return data;
  }, [billingTermsInput, c.id, calculationInputError]);

  useEffect(() => {
    if (calculationInputError) {
      setCalculating(false);
      return undefined;
    }

    setCalculating(true);
    const timeoutId = window.setTimeout(() => {
      calculateTerms({ silent: true });
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [calculateTerms, calculationInputError]);

  function handleHandOff() {
    setHandOffNotice(true);
    onHandOff?.();
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting || calculating) return;
    if (!previewReady) {
      await calculateTerms();
      return;
    }
    const contractBlock = getOrthoContractBlock({
      patient,
      doctor,
      guardian: form,
    });
    if (contractBlock) {
      toast.error(contractBlock.message);
      return;
    }
    setSubmitting(true);
    const { data, error } = await supabase.rpc("setup_ortho_billing", {
      p_case_id: c.id,
      p_input: billingTermsInput,
    });
    if (error) {
      setSubmitting(false);
      toast.error(error.message || "Could not setup Ortho billing.");
      return;
    }
    const { data: contractBilling, error: contractError } = await supabase.rpc("update_ortho_contract_guardian_details", {
      p_case_id: c.id,
      p_input: {
        guardian_full_name: form.guardian_full_name || null,
        guardian_relationship: form.guardian_relationship || null,
      },
    });
    setSubmitting(false);
    if (contractError) {
      toast.error(contractError.message || "Billing saved, but contract details could not be completed.");
      return;
    }
    toast.success("Ortho billing set up.");
    try {
      const doc = await buildOrthoContractPdf({
        contract: { ...row, billing: contractBilling || data },
        patient,
        clinic,
        doctorName: doctorProfile.fullName,
        doctorPrcLicense: doctorProfile.prcLicense,
      });
      doc.save(pdfFilename(patient, "ortho_contract"));
    } catch (err) {
      toast.warning(err?.message || "Billing saved, but contract PDF could not be generated.");
    }
    onSaved?.();
  }

  const content = wizardStep === "case" && showWizard ? (
      <div className="space-y-4">
        <WizardSteps current="case" />
        <div className="rounded-xl border border-teal-100 bg-teal-50 px-3 py-2 text-sm text-teal-900">
          <strong>Case details are already saved.</strong>
          <span className="block text-xs mt-0.5 text-teal-700">This patient can now be handed to checkout Step 1, or you can continue to billing setup.</span>
        </div>
        <div className="grid gap-3">
          <ReadonlyField label="Case type" value={labelize(c.case_type || "fixed")} />
          <ReadonlyField label="System" value={labelize(c.system_name || c.system || "Ortho case")} />
          <ReadonlyField label="Estimated adjustment visits" value={form.estimated_total_visits || "Not set"} />
          <ReadonlyField label="Chief complaint" value={c.chief_complaint || "Not set"} />
          <ReadonlyField label="Primary doctor" value={c.primary_doctor_name || "Primary doctor saved"} />
          <ReadonlyField label="Notes for front desk" value={c.notes || "None"} />
        </div>
        {handOffNotice ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="ortho-billing-handoff-notice">
            <strong>Successfully handed off to checkout.</strong>
            <span className="block text-xs mt-0.5 text-emerald-700">Front desk will complete Ortho billing in checkout Step 1.</span>
          </div>
        ) : null}
        <div className="pt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {canHandOff ? (
              <button type="button" onClick={handleHandOff} className="h-10 px-4 rounded-lg border border-amber-200 bg-amber-50 text-sm font-semibold text-amber-800 hover:bg-amber-100">
                {handOffNotice ? "Handed off" : "Hand off to checkout"}
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600">Cancel</button>
            <button type="button" onClick={() => setWizardStep("billing")} className="h-10 px-4 rounded-lg bg-teal-600 text-sm font-semibold text-white">
              Continue to billing
            </button>
          </div>
        </div>
      </div>
  ) : (
      <form onSubmit={submit} className="space-y-4">
        {showWizard ? <WizardSteps current="billing" /> : null}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
          <strong className="text-slate-900">{labelize(c.system_name || c.system || "Ortho case")}</strong>
          <span className="mx-2">·</span>
          {labelize(c.case_type || "fixed")}
          {c.system_default_price_centavos ? (
            <span className="ml-2 text-slate-500">Default: {money(c.system_default_price_centavos)}</span>
          ) : null}
        </div>
        {handOffNotice ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="ortho-billing-handoff-notice">
            <strong>Successfully handed off to checkout.</strong>
            <span className="block text-xs mt-0.5 text-emerald-700">Front desk will complete Ortho billing in checkout Step 1.</span>
          </div>
        ) : null}
        <Field label="Package price">
          <MoneyInput
            value={form.package_price}
            onChange={(package_price) => patchForm({ package_price })}
            inputProps={{ "data-testid": "ortho-billing-package-price" }}
          />
        </Field>
        <Field label="Billing model">
          <Segmented
            value={form.billing_mode}
            options={[
              { value: "payment_plan", label: "Payment plan" },
              ...(allowFullPayment ? [{ value: "full_payment", label: "Full payment" }] : []),
            ]}
            onChange={(billing_mode) => patchForm({ billing_mode, number_of_payments: billing_mode === "full_payment" ? 1 : form.number_of_payments })}
          />
          {!allowFullPayment ? <p className="mt-1 text-xs text-slate-500">Full payment is hidden by Ortho settings.</p> : null}
        </Field>
        {!isFullPayment ? (
          <Field label="Down payment">
            <MoneyInput
              value={form.down_payment}
              onChange={(down_payment) => patchForm({ down_payment })}
              inputProps={{ "data-testid": "ortho-billing-down-payment" }}
            />
          </Field>
        ) : null}
        {!isFullPayment ? (
          <Field label="Build payment schedule by">
            <Segmented
              value={form.payment_plan_basis}
              options={[
                { value: "payment_count", label: "Number of payments" },
                { value: "preferred_amount", label: "Preferred monthly payment" },
              ]}
              onChange={(payment_plan_basis) => patchForm({ payment_plan_basis })}
            />
            <p className="mt-1 text-xs text-slate-500">Both options remain adjustment-based. Dental OS expects one regular payment at each adjustment visit.</p>
          </Field>
        ) : null}
        {!isFullPayment && form.payment_plan_basis === "preferred_amount" ? (
          <Field label="Patient's preferred monthly payment">
            <MoneyInput
              value={form.preferred_payment}
              onChange={(preferred_payment) => patchForm({ preferred_payment })}
              inputProps={{ "data-testid": "ortho-billing-preferred-payment" }}
            />
            <p className="mt-1 text-xs text-slate-500">The final payment can be lower so the total never exceeds the package balance.</p>
          </Field>
        ) : null}
        {!isFullPayment ? (
          <Field label="Estimated adjustment visits">
            <input
              type="number"
              min={currentAdjustmentVisitEstimate}
              className={INPUT_CLASS}
              data-testid="ortho-billing-estimated-visits"
              value={form.estimated_total_visits}
              onChange={(e) => patchForm({ estimated_total_visits: e.target.value })}
            />
            <p className="mt-1 text-xs text-slate-500">Current case estimate: {currentAdjustmentVisitEstimate} adjustments. Installation Day 0 and Debonding Day are excluded.</p>
          </Field>
        ) : null}
        {!isFullPayment && form.payment_plan_basis === "payment_count" ? (
          <Field label="Number of payments">
            <input
              type="number"
              min="1"
              className={INPUT_CLASS}
              data-testid="ortho-billing-payment-count"
              value={form.number_of_payments}
              onChange={(e) => patchForm({ number_of_payments: e.target.value })}
            />
            <p className="mt-1 text-xs text-slate-500">Installation is Day 0. The down payment is separate, and regular payments begin at Adjustment 1.</p>
          </Field>
        ) : null}
        {minorPatient ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-semibold text-amber-950">Guardian required for contract</p>
            <p className="mt-1 text-xs text-amber-800">This patient is under 18 today. These fields print on the contract and are separate from payment responsibility.</p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Guardian full name">
                <input
                  className={INPUT_CLASS}
                  value={form.guardian_full_name}
                  onChange={(e) => patchForm({ guardian_full_name: e.target.value })}
                  placeholder="Full legal name"
                />
              </Field>
              <Field label="Guardian relationship to patient">
                <input
                  className={INPUT_CLASS}
                  value={form.guardian_relationship}
                  onChange={(e) => patchForm({ guardian_relationship: e.target.value })}
                  placeholder="e.g. Mother"
                />
              </Field>
            </div>
          </div>
        ) : null}
        <OrthoPlanCard termsPreview={termsPreview} previewFits={previewFits} isFullPayment={isFullPayment} />
        <DialogActions
          submitting={submitting || calculating}
          submittingLabel={calculating ? "Calculating..." : "Saving..."}
          submitDisabled={Boolean(termsPreview && !previewFits)}
          submitLabel={previewReady ? "Save billing & print contract" : termsPreview ? "Increase visits or payment" : "Complete payment details"}
          onClose={onClose}
          onBack={showWizard ? () => setWizardStep("case") : onBack}
          onHandOff={canHandOff ? handleHandOff : undefined}
          handOffComplete={handOffNotice}
        />
      </form>
  );

  if (embedded) return content;

  return (
    <Modal title="Setup Ortho Billing" onClose={onClose}>
      {content}
    </Modal>
  );
}

function WizardSteps({ current }) {
  const steps = [
    { id: "case", label: "1 Case details" },
    { id: "billing", label: "2 Billing" },
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-1.5 flex gap-1" data-testid="ortho-wizard-steps">
      {steps.map((step) => {
        const active = current === step.id;
        return (
          <div
            key={step.id}
            className={`flex-1 rounded-lg px-3 py-2 text-center text-xs font-semibold ${active ? "bg-slate-900 text-white" : "text-slate-500"}`}
          >
            {step.label}
          </div>
        );
      })}
    </div>
  );
}

function ReadonlyField({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function PauseCaseDialog({ row, onClose, onSaved }) {
  const c = row.case || {};
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    if (!reason.trim()) {
      toast.error("Pause reason is required.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.rpc("pause_ortho_case", {
      p_case_id: c.id,
      p_reason: reason.trim(),
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not pause Ortho case.");
      return;
    }
    toast.success("Ortho case paused.");
    onSaved?.();
  }

  return (
    <Modal title="Pause Ortho Case" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <strong className="text-slate-900">{labelize(c.system_name || c.system || "Ortho case")}</strong>
          <span className="block mt-0.5 text-xs text-slate-500">Charges do not auto-fire while the case is paused. Billing terms stay unchanged.</span>
        </div>
        <Field label="Pause reason">
          <textarea
            className="min-h-[88px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Patient travelling for two months."
          />
        </Field>
        <DialogActions submitting={submitting} submitLabel="Pause case" onClose={onClose} />
      </form>
    </Modal>
  );
}

function ReconcileCaseDialog({ row, settings, onClose, onSaved }) {
  const c = row.case || {};
  const b = row.billing || {};
  const balance = contractRemainingCentavos(row);
  const overpayment = Number(row.balance?.overpayment_centavos || 0);
  const defaultDisposition = balance <= 0
    ? "mark_complete"
    : (settings?.reconciliation_default_disposition === "always_ask"
      ? "collect_remaining"
      : settings?.reconciliation_default_disposition || "collect_remaining");
  const [resolution, setResolution] = useState(defaultDisposition);
  const [amount, setAmount] = useState(centavosToPesoInput(balance));
  const [notes, setNotes] = useState("");
  const [overpaymentDecision, setOverpaymentDecision] = useState(overpayment > 0 ? "keep_on_account" : "");
  const [overpaymentNotes, setOverpaymentNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const needsAmount = resolution === "write_off" || resolution === "discount";
  const threshold = Number(settings?.reconciliation_approval_threshold_centavos || 500000);
  const amountCents = needsAmount ? pesosToCentavos(amount) : 0;
  const approvalWarning = needsAmount && amountCents > threshold;

  function changeResolution(next) {
    setResolution(next);
    if (next === "write_off" || next === "discount") setAmount(centavosToPesoInput(balance));
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    if (!notes.trim()) {
      toast.error("Reason / notes are required.");
      return;
    }
    if (needsAmount && amountCents <= 0) {
      toast.error("Enter an amount for the write-off or discount.");
      return;
    }
    if (overpayment > 0 && !overpaymentDecision) {
      toast.error("Choose how to handle the overpayment.");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.rpc("reconcile_ortho_case", {
      p_case_id: c.id,
      p_input: {
        resolution_type: resolution,
        amount_centavos: needsAmount ? amountCents : undefined,
        notes: notes.trim(),
        final_status: resolution === "mark_complete" ? "completed" : undefined,
      },
    });
    if (!error && overpayment > 0) {
      const { error: overpaymentError } = await supabase.rpc("record_ortho_overpayment_decision", {
        p_case_id: c.id,
        p_input: {
          decision: overpaymentDecision,
          notes: overpaymentNotes.trim() || notes.trim(),
        },
      });
      if (overpaymentError) {
        setSubmitting(false);
        toast.error(overpaymentError.message || "Reconciliation saved, but overpayment decision failed.");
        return;
      }
    }
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not reconcile Ortho case.");
      return;
    }
    toast.success("Ortho case reconciled.");
    onSaved?.();
  }

  return (
    <Modal title="Close / Reconcile Ortho Case" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <div className="font-semibold text-slate-950">{labelize(c.system_name || c.system || "Ortho case")}</div>
          <div className="mt-1 text-xs text-slate-500">
            Adjustments done: {(row.visits || []).filter((entry) => (
              entry?.ortho_visit?.visit_type === "adjustment"
              && !entry?.ortho_visit?.voided_at
            )).length} of {c.estimated_total_visits || b.estimated_total_visits || "?"} estimated
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <ReadonlyField label="Package price" value={money(b.package_price_centavos || 0)} />
          <ReadonlyField label="Total collected" value={money(row.balance?.total_collected_centavos || 0)} />
          <ReadonlyField label="Balance" value={money(balance)} />
        </div>

        {overpayment > 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <div className="font-semibold">Overpayment {money(overpayment)}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                { value: "refund", label: "Refund" },
                { value: "apply_future", label: "Apply to future work" },
                { value: "keep_on_account", label: "Keep on account" },
              ].map((option) => (
                <button key={option.value} type="button" onClick={() => setOverpaymentDecision(option.value)} className={pill(overpaymentDecision === option.value)}>
                  {option.label}
                </button>
              ))}
            </div>
            <input
              className={`${INPUT_CLASS} mt-2`}
              value={overpaymentNotes}
              onChange={(e) => setOverpaymentNotes(e.target.value)}
              placeholder="Optional overpayment note"
            />
          </div>
        ) : null}

        <Field label="What happens to the balance?">
          <div className="space-y-2">
            {[
              { value: "collect_remaining", label: "Collect remaining at checkout" },
              { value: "keep_ar", label: "Keep as AR" },
              { value: "write_off", label: "Write off / waive" },
              { value: "discount", label: "Apply discount" },
              { value: "mark_complete", label: "Mark complete" },
            ].map((option) => (
              <label key={option.value} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${resolution === option.value ? "border-teal-300 bg-teal-50 text-teal-900" : "border-slate-200 bg-white text-slate-700"}`}>
                <input
                  type="radio"
                  checked={resolution === option.value}
                  onChange={() => changeResolution(option.value)}
                  className="h-4 w-4 border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                {option.label}
              </label>
            ))}
          </div>
        </Field>

        {needsAmount ? (
          <Field label={resolution === "discount" ? "Discount amount" : "Write-off amount"}>
            <MoneyInput value={amount} onChange={setAmount} />
            {approvalWarning ? (
              <p className="mt-1 text-xs font-semibold text-amber-700">
                This exceeds the owner approval threshold of {money(threshold)}.
              </p>
            ) : null}
          </Field>
        ) : null}

        <Field label="Reason / notes">
          <textarea
            className="min-h-[96px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Required. Explain why this case is being reconciled this way."
          />
        </Field>

        <DialogActions submitting={submitting} submitLabel="Confirm reconciliation" onClose={onClose} />
      </form>
    </Modal>
  );
}

const RETAINER_TYPES = [
  { value: "removable_wire", label: "Removable Wire" },
  { value: "clear_removable", label: "Clear Removable" },
  { value: "fixed_wire", label: "Fixed Wire" },
  { value: "none", label: "None" },
  { value: "other", label: "Other" },
];

const LAB_RETAINER_TYPES = ["removable_wire", "clear_removable", "other"];

function RetainerSetupDialog({ row, appointmentId, settings, onClose, onSaved }) {
  const c = row.case || {};
  const [form, setForm] = useState({
    upper_retainer_type: "removable_wire",
    lower_retainer_type: "removable_wire",
    upper_retainer_other_description: "",
    lower_retainer_other_description: "",
    impression_timing: appointmentId ? "same_day" : "scheduled",
    impression_scheduled_at: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const upperCost = retainerTypeCost(form.upper_retainer_type, settings);
  const lowerCost = retainerTypeCost(form.lower_retainer_type, settings);
  const fixedTotal = (form.upper_retainer_type === "fixed_wire" ? upperCost : 0)
    + (form.lower_retainer_type === "fixed_wire" ? lowerCost : 0);
  const labTotal = (LAB_RETAINER_TYPES.includes(form.upper_retainer_type) ? upperCost : 0)
    + (LAB_RETAINER_TYPES.includes(form.lower_retainer_type) ? lowerCost : 0);
  const hasLab = labTotal > 0;
  const total = upperCost + lowerCost;

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    if (hasLab && form.impression_timing === "same_day" && !appointmentId) {
      toast.error("Same-day impression requires today's debonding appointment.");
      return;
    }
    if (form.upper_retainer_type === "other" && !form.upper_retainer_other_description.trim()) {
      toast.error("Specify the upper Other retainer type.");
      return;
    }
    if (form.lower_retainer_type === "other" && !form.lower_retainer_other_description.trim()) {
      toast.error("Specify the lower Other retainer type.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.rpc("setup_ortho_retainer", {
      p_case_id: c.id,
      p_input: {
        appointment_id: appointmentId || null,
        upper_retainer_type: form.upper_retainer_type,
        lower_retainer_type: form.lower_retainer_type,
        upper_retainer_other_description: form.upper_retainer_other_description.trim() || null,
        lower_retainer_other_description: form.lower_retainer_other_description.trim() || null,
        impression_timing: hasLab ? form.impression_timing : "not_needed",
        impression_scheduled_at: form.impression_scheduled_at || null,
        notes: form.notes.trim() || null,
      },
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not set up retainer.");
      return;
    }
    toast.success("Retainer setup saved.");
    onSaved?.();
  }

  return (
    <Modal title="Retainer Setup" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <div className="font-semibold text-slate-950">{labelize(c.system_name || c.system || "Completed ortho case")}</div>
          <div className="mt-1 text-xs text-slate-500">
            Charge mode: {settings?.retainer_charge_mode === "split" ? "Split impression / installation" : "Full at impression"}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Upper retainer">
            <select className={INPUT_CLASS} value={form.upper_retainer_type} onChange={(e) => setField("upper_retainer_type", e.target.value)}>
              {RETAINER_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            {form.upper_retainer_type === "other" ? (
              <input
                className={`${INPUT_CLASS} mt-2`}
                value={form.upper_retainer_other_description}
                onChange={(e) => setField("upper_retainer_other_description", e.target.value)}
                placeholder="Specify retainer type"
              />
            ) : null}
            <p className="mt-1 text-xs text-slate-500">{money(upperCost)}</p>
          </Field>
          <Field label="Lower retainer">
            <select className={INPUT_CLASS} value={form.lower_retainer_type} onChange={(e) => setField("lower_retainer_type", e.target.value)}>
              {RETAINER_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            {form.lower_retainer_type === "other" ? (
              <input
                className={`${INPUT_CLASS} mt-2`}
                value={form.lower_retainer_other_description}
                onChange={(e) => setField("lower_retainer_other_description", e.target.value)}
                placeholder="Specify retainer type"
              />
            ) : null}
            <p className="mt-1 text-xs text-slate-500">{money(lowerCost)}</p>
          </Field>
        </div>

        {hasLab ? (
          <Field label="Impression timing">
            <div className="flex flex-wrap gap-2">
              <ToggleButton active={form.impression_timing === "same_day"} onClick={() => setField("impression_timing", "same_day")}>
                Same day
              </ToggleButton>
              <ToggleButton active={form.impression_timing === "scheduled"} onClick={() => setField("impression_timing", "scheduled")}>
                Schedule separately
              </ToggleButton>
            </div>
            {form.impression_timing === "scheduled" ? (
              <input
                type="date"
                value={form.impression_scheduled_at}
                onChange={(e) => setField("impression_scheduled_at", e.target.value)}
                className={`${INPUT_CLASS} mt-2 max-w-[220px]`}
              />
            ) : null}
          </Field>
        ) : null}

        <div className="grid grid-cols-1 gap-2 rounded-xl border border-teal-100 bg-teal-50 p-3 text-sm text-teal-950 sm:grid-cols-3">
          <ReadonlyField label="Total retainer" value={money(total)} />
          <ReadonlyField label="Fixed Wire at debonding" value={money(fixedTotal)} />
          <ReadonlyField label="Impression / installation" value={money(labTotal)} />
        </div>

        <Field label="Notes">
          <textarea
            className="min-h-[80px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
            placeholder="Optional retainer note"
          />
        </Field>

        <DialogActions submitting={submitting} submitLabel="Save retainer setup" onClose={onClose} />
      </form>
    </Modal>
  );
}

function OrthoVisitDialog({ row, visitType, appointmentId, wireOptions, onClose, onSaved }) {
  const { activeClinicId } = useAuth();
  const c = row.case || {};
  const b = row.billing || {};
  const visits = useMemo(
    () => (row.visits || []).filter((visitRow) => (
      !visitRow.ortho_visit?.voided_at
      && visitRow.procedure_instance?.status !== "voided"
      && visitRow.procedure_visit?.outcome !== "voided"
    )),
    [row.visits],
  );
  const currentAppointmentVisit = useMemo(
    () => visits.find((visitRow) => {
      const ov = visitRow.ortho_visit || {};
      const pv = visitRow.procedure_visit || {};
      return ov.visit_type === visitType
        && (ov.appointment_id === appointmentId || pv.appointment_id === appointmentId);
    }) || null,
    [appointmentId, visitType, visits],
  );
  const latest = useMemo(
    () => visits
      .filter((visitRow) => visitRow !== currentAppointmentVisit)
      .sort((a, b) => Number(b.ortho_visit?.visit_number || 0) - Number(a.ortho_visit?.visit_number || 0))[0]?.ortho_visit || null,
    [currentAppointmentVisit, visits],
  );
  const savedDraft = currentAppointmentVisit?.ortho_visit || null;
  const wireSource = savedDraft || latest;
  const initialUpperWire = wireSelectionFromVisit(wireSource, "upper", wireOptions);
  const initialLowerWire = wireSelectionFromVisit(wireSource, "lower", wireOptions);
  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [chargePreview, setChargePreview] = useState(null);
  const [chargePreviewError, setChargePreviewError] = useState("");
  const [form, setForm] = useState({
    arch_bonded_upper: visitType === "bonding",
    arch_bonded_lower: visitType === "bonding",
    upper_wire_option_id: initialUpperWire.optionId || "",
    lower_wire_option_id: initialLowerWire.optionId || "",
    upper_wire_material: initialUpperWire.material,
    upper_wire_size: initialUpperWire.size,
    upper_wire_custom: initialUpperWire.custom,
    lower_wire_material: initialLowerWire.material,
    lower_wire_size: initialLowerWire.size,
    lower_wire_custom: initialLowerWire.custom,
    elastics_prescribed: "None this visit",
    oh_status: latest?.oh_status || "good",
    next_visit_interval_weeks: latest?.next_visit_interval_weeks || 4,
    notes: "",
    photoNames: [],
    photoFiles: [],
  });
  const displayAdjustmentNumber = orthoAdjustmentNumber(currentAppointmentVisit)
    || nextOrthoAdjustmentNumber(visits);
  const previewSequenceNumber = visitType === "adjustment" ? displayAdjustmentNumber : 0;
  const eventLabel = visitType === "bonding"
    ? "Installation Day · Day 0"
    : `Adjustment ${displayAdjustmentNumber}`;
  const chargeDue = chargePreview?.charge_due_centavos;
  const balanceAfter = chargePreview?.contract_uncharged_after_centavos;
  const title = visitType === "bonding" ? "Record Installation Day" : `Record Adjustment ${displayAdjustmentNumber}`;

  useEffect(() => {
    let cancelled = false;
    setChargePreview(null);
    setChargePreviewError("");

    if (!c.id || (visitType === "adjustment" && !displayAdjustmentNumber)) {
      setChargePreviewError("Charge preview is unavailable.");
      return () => { cancelled = true; };
    }

    (async () => {
      const { data, error } = await supabase.rpc("get_ortho_visit_charge_preview", {
        p_case_id: c.id,
        p_visit_number: previewSequenceNumber,
        p_visit_type: visitType,
      });
      if (cancelled) return;
      if (error) {
        setChargePreviewError(error.message || "Charge preview is unavailable.");
        return;
      }
      setChargePreview(data);
    })();

    return () => { cancelled = true; };
  }, [c.id, displayAdjustmentNumber, previewSequenceNumber, visitType]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setWireSelection(side, selection) {
    const payload = wireSelectionPayload(selection, wireOptions);
    setForm((prev) => ({
      ...prev,
      [`${side}_wire_material`]: selection.material || "",
      [`${side}_wire_size`]: selection.size || "",
      [`${side}_wire_custom`]: selection.custom || "",
      [`${side}_wire_option_id`]: payload.optionId || "",
    }));
  }

  function sameAsLast(field) {
    if (!latest) return;
    setForm((prev) => ({ ...prev, [field]: latest[field] || (field === "elastics_prescribed" ? "None this visit" : "") }));
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    if (!appointmentId) {
      toast.error("Open this case from Doctor View so the visit can attach to today's appointment.");
      return;
    }
    if (visitType === "bonding" && !form.arch_bonded_upper && !form.arch_bonded_lower) {
      toast.error("Select at least one bonded arch.");
      return;
    }
    // Bonding guard: don't silently save a bonded arch with no wire on record.
    // Soft confirm (not a hard block) — can still override.
    if (visitType === "bonding") {
      const upperMissing = form.arch_bonded_upper && !formatWireSelection({ material: form.upper_wire_material, size: form.upper_wire_size, custom: form.upper_wire_custom });
      const lowerMissing = form.arch_bonded_lower && !formatWireSelection({ material: form.lower_wire_material, size: form.lower_wire_size, custom: form.lower_wire_custom });
      if (upperMissing || lowerMissing) {
        const which = upperMissing && lowerMissing ? "both arches" : upperMissing ? "the upper arch" : "the lower arch";
        const ok = window.confirm(`You bonded ${which} but didn't pick a wire for it.\n\nSave without recording the wire?`);
        if (!ok) return;
      }
    }
    const visitBlock = getOrthoVisitStartBlock({ row, visitType });
    if (visitBlock) {
      toast.error(visitBlock.message);
      return;
    }
    setSubmitting(true);

    // Upload picked photos to Patient Files (real storage) first, so the visit
    // stores real storage paths and the photos show in Files + the viewer.
    let uploadedPhotos = [];
    const pendingFiles = Array.isArray(form.photoFiles) ? form.photoFiles : [];
    if (pendingFiles.length && c.patient_id && activeClinicId) {
      const stage = orthoStageForVisitType(visitType);
      try {
        const uploads = await Promise.all(pendingFiles.map((file) => uploadPatientFile({
          patientId: c.patient_id,
          clinicId: activeClinicId,
          file,
          category: "ortho_case_photos",
          orthoCaseId: c.id,
          orthoStage: stage,
        })));
        uploadedPhotos = uploads.map((res, i) => ({
          name: pendingFiles[i]?.name || res?.original_filename || "photo",
          storage_path: res?.storage_path || null,
          file_id: res?.id || null,
          stage,
          captured_at: new Date().toISOString(),
          source: `ortho_${visitType}_form`,
        }));
      } catch (uploadErr) {
        setSubmitting(false);
        toast.error(uploadErr?.message || "Could not upload photos. Try again.");
        return;
      }
    } else if (pendingFiles.length) {
      toast.error("Photos weren't attached (missing patient or clinic) — saving the visit without them.");
    }

    const upperWirePayload = wireSelectionPayload({ material: form.upper_wire_material, size: form.upper_wire_size, custom: form.upper_wire_custom }, wireOptions);
    const lowerWirePayload = wireSelectionPayload({ material: form.lower_wire_material, size: form.lower_wire_size, custom: form.lower_wire_custom }, wireOptions);
    const visitPayload = {
        idempotency_key: buildOrthoVisitIdempotencyKey({ caseId: c.id, appointmentId, visitType }),
        appointment_id: appointmentId,
        visit_type: visitType,
        arch_bonded_upper: !!form.arch_bonded_upper,
        arch_bonded_lower: !!form.arch_bonded_lower,
        upper_wire_option_id: upperWirePayload.optionId,
        lower_wire_option_id: lowerWirePayload.optionId,
        upper_wire_material: upperWirePayload.material,
        upper_wire_size: upperWirePayload.size,
        upper_wire_custom: upperWirePayload.custom,
        lower_wire_material: lowerWirePayload.material,
        lower_wire_size: lowerWirePayload.size,
        lower_wire_custom: lowerWirePayload.custom,
        elastics_prescribed: form.elastics_prescribed || "None this visit",
        oh_status: form.oh_status,
        next_visit_interval_weeks: Number(form.next_visit_interval_weeks || 4),
        notes: form.notes.trim() || null,
        photos: uploadedPhotos,
      };
    const { error } = savedDraft?.id
      ? await supabase.rpc("update_ortho_visit_draft", { p_visit_id: savedDraft.id, p_input: visitPayload })
      : await supabase.rpc("record_ortho_adjustment_visit", { p_case_id: c.id, p_input: visitPayload });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not record Ortho visit.");
      return;
    }
    toast.success(`${labelize(visitType)} visit saved. Sending to checkout.`);
    onSaved();
  }

  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 gap-2 rounded-xl border border-teal-100 bg-teal-50 p-3 text-sm text-teal-950 sm:grid-cols-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-700">Case event</div>
            <div className="font-semibold">
              {eventLabel}{visitType === "adjustment" ? ` of ${c.estimated_total_visits || b.estimated_total_visits || "?"}` : ""}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-700">Charge due</div>
            <div className="font-semibold">{chargePreview ? money(chargeDue) : chargePreviewError ? "Unavailable" : "Calculating..."}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-700">Balance after</div>
            <div className="font-semibold">{chargePreview ? money(balanceAfter) : chargePreviewError ? "Unavailable" : "Calculating..."}</div>
          </div>
        </div>
        {chargePreviewError ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {chargePreviewError} The database will still calculate the authoritative charge when the visit is saved.
          </div>
        ) : null}

        {visitType === "adjustment" && latest ? (
          <LastVisitSummary latest={latest} />
        ) : null}

        <Field label={visitType === "bonding" ? "Arch bonded" : "Arch treated"}>
          <div className="flex flex-wrap gap-2">
            <ToggleButton active={form.arch_bonded_upper} onClick={() => setField("arch_bonded_upper", !form.arch_bonded_upper)}>Upper</ToggleButton>
            <ToggleButton active={form.arch_bonded_lower} onClick={() => setField("arch_bonded_lower", !form.arch_bonded_lower)}>Lower</ToggleButton>
          </div>
        </Field>

        <Field label="Wire placed">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <OrthoWireGaugeSelect
              label="Upper"
              material={form.upper_wire_material}
              size={form.upper_wire_size}
              custom={form.upper_wire_custom}
              options={wireOptions}
              previous={wireSelectionFromVisit(latest, "upper", wireOptions)}
              onChange={(selection) => setWireSelection("upper", selection)}
            />
            <OrthoWireGaugeSelect
              label="Lower"
              material={form.lower_wire_material}
              size={form.lower_wire_size}
              custom={form.lower_wire_custom}
              options={wireOptions}
              previous={wireSelectionFromVisit(latest, "lower", wireOptions)}
              onChange={(selection) => setWireSelection("lower", selection)}
            />
          </div>
        </Field>

        <Field label="Elastics prescribed">
          <div className="flex gap-2">
            <input
              className={INPUT_CLASS}
              value={form.elastics_prescribed}
              onChange={(e) => setField("elastics_prescribed", e.target.value)}
              placeholder="None this visit"
            />
            {latest?.elastics_prescribed ? (
              <button type="button" onClick={() => sameAsLast("elastics_prescribed")} className="shrink-0 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                Same as last
              </button>
            ) : null}
          </div>
        </Field>

        <Field label="OH status">
          <Segmented
            value={form.oh_status}
            options={[
              { value: "good", label: "Good" },
              { value: "fair", label: "Fair" },
              { value: "poor", label: "Poor" },
            ]}
            onChange={(oh_status) => setField("oh_status", oh_status)}
          />
        </Field>

        <Field label="Next visit interval">
          <Segmented
            value={String(form.next_visit_interval_weeks)}
            options={[4, 5, 6, 8].map((n) => ({ value: String(n), label: `${n}w` }))}
            onChange={(value) => setField("next_visit_interval_weeks", Number(value))}
          />
        </Field>

        <Field label="Notes">
          <textarea
            className="min-h-[80px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
            placeholder={visitType === "bonding" ? "Bonding notes..." : "Adjustment notes..."}
          />
        </Field>

        <Field label="Photos">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => cameraInputRef.current?.click()} className="inline-flex h-9 items-center rounded-lg bg-teal-600 px-3 text-sm font-semibold text-white hover:bg-teal-700">
              Take photo
            </button>
            <button type="button" onClick={() => uploadInputRef.current?.click()} className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              Upload files
            </button>
            <span className="text-sm text-slate-500">{form.photoNames.length ? `${form.photoNames.length} selected` : "Optional"}</span>
          </div>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={(e) => addPhotoNames(e, setForm)} className="sr-only" />
          <input ref={uploadInputRef} type="file" multiple accept="image/*" onChange={(e) => addPhotoNames(e, setForm)} className="sr-only" />
          {form.photoNames.length ? <p className="mt-1 text-xs text-slate-500">{form.photoNames.join(", ")}</p> : null}
        </Field>

        <DialogActions submitting={submitting} submitLabel="Save & send to checkout" onClose={onClose} />
      </form>
    </Modal>
  );
}

function LastVisitSummary({ latest }) {
  const upperWire = formatWireSelection(wireSelectionFromVisit(latest, "upper", [])) || String(latest?.upper_wire_label_snapshot || "").replace(/^Other:\s*/i, "").trim();
  const lowerWire = formatWireSelection(wireSelectionFromVisit(latest, "lower", [])) || String(latest?.lower_wire_label_snapshot || "").replace(/^Other:\s*/i, "").trim();
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Last visit summary</div>
      <div className="mt-1 font-semibold text-slate-900">{orthoEventLabel(latest)}</div>
      <div className="mt-1 text-xs text-slate-600">
        {[
          upperWire ? `Upper ${upperWire}` : null,
          lowerWire ? `Lower ${lowerWire}` : null,
          latest.elastics_prescribed ? `Elastics: ${latest.elastics_prescribed}` : null,
          latest.oh_status ? `OH ${labelize(latest.oh_status)}` : null,
        ].filter(Boolean).join(" · ")}
      </div>
      {latest.notes ? <div className="mt-1 text-xs italic text-slate-500">"{latest.notes}"</div> : null}
    </div>
  );
}

function ToggleButton({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} className={pill(active)}>
      {children}
    </button>
  );
}

function Modal({ title, children, onClose, tutorialId = null, contentClassName = "p-5 max-h-[80vh] overflow-y-auto" }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl overflow-hidden" data-tutorial-id={tutorialId || undefined}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-950">{title}</h3>
          <button type="button" onClick={onClose} data-tutorial-id={tutorialId ? `${tutorialId}-close` : undefined} className="text-sm font-semibold text-slate-500 hover:text-slate-900">Close</button>
        </div>
        <div className={contentClassName}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Segmented({ value, options, onChange, tutorialId = null, tutorialIdsByValue = null, grouped = false }) {
  return (
    <div
      className={grouped ? "inline-flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1" : "flex flex-wrap gap-2"}
      data-tutorial-id={tutorialId || undefined}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          data-tutorial-id={tutorialIdsByValue?.[o.value] || (tutorialId ? `${tutorialId}-${o.value}` : undefined)}
          className={grouped
            ? `h-9 rounded-lg px-4 text-sm font-semibold ${value === o.value ? "bg-teal-600 text-white shadow-sm" : "text-slate-600 hover:bg-white"}`
            : pill(value === o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function MoneyInput({ value, onChange, inputProps = {} }) {
  return (
    <div className="flex rounded-lg border border-slate-200 overflow-hidden">
      <span className="h-10 px-3 flex items-center bg-slate-50 text-slate-500 text-sm">₱</span>
      <input
        className="h-10 flex-1 px-3 outline-none text-sm"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          emitTutorialFieldChanged(e);
        }}
        inputMode="decimal"
        placeholder="0.00"
        {...inputProps}
      />
    </div>
  );
}

function emitTutorialFieldChanged(event) {
  const tutorialId = event?.currentTarget?.dataset?.tutorialId;
  if (!tutorialId || typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(`tutorial:${tutorialId}-changed`));
}

function DialogActions({
  submitting,
  submittingLabel = "Saving...",
  submitDisabled = false,
  submitLabel,
  onClose,
  onBack,
  onHandOff,
  handOffComplete = false,
  submitTutorialId = null,
}) {
  return (
    <div className="pt-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {onBack ? (
          <button type="button" onClick={onBack} disabled={submitting} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Previous</button>
        ) : null}
        {onHandOff ? (
          <button type="button" onClick={onHandOff} disabled={submitting} className="h-10 px-4 rounded-lg border border-amber-200 bg-amber-50 text-sm font-semibold text-amber-800 hover:bg-amber-100">
            {handOffComplete ? "Handed off" : "Hand off to checkout"}
          </button>
        ) : null}
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <button type="button" onClick={onClose} disabled={submitting} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600">Cancel</button>
        <button type="submit" disabled={submitting || submitDisabled} data-tutorial-id={submitTutorialId || undefined} className="h-10 px-4 rounded-lg bg-teal-600 text-sm font-semibold text-white disabled:opacity-60">
          {submitting ? submittingLabel : submitLabel}
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const label = labelize(status || "pending");
  return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">{label}</span>;
}

function TransferCaseBadge() {
  return (
    <span
      data-tutorial-id="ortho-transfer-case-badge"
      className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-violet-700"
      title="Imported from another clinic. Bonding already happened — first visit here is an adjustment."
    >
      Transfer case
    </span>
  );
}

function OrthoVisitProgressBar({ visitsDone, totalVisits, isClosed }) {
  const total = Math.max(Number(totalVisits || 0), 1);
  const done = Math.max(0, Math.min(Number(visitsDone || 0), total));
  const pct = Math.round((done / total) * 100);
  const fillColor = isClosed ? "bg-emerald-500" : "bg-teal-500";
  return (
    <div className="mt-2 flex items-center gap-3" aria-label={`Ortho progress: ${done} of ${total} visits, ${pct}%`}>
      <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full ${fillColor} transition-[width] duration-300`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-semibold tabular-nums text-slate-600">{pct}%</span>
    </div>
  );
}

function pill(active) {
  return `h-9 rounded-lg px-3 text-sm font-semibold border ${active ? "bg-teal-600 border-teal-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`;
}

function money(centavos) {
  return `₱${Math.round(Number(centavos || 0) / 100).toLocaleString("en-US")}`;
}

function formatActivityDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatOrthoEventDate(value) {
  if (!value) return "Date not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date not recorded";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function orthoTimelineStatusText(current, previous) {
  if (!current) return "";
  if (!previous) return " — recorded";
  return current === previous ? " — carried forward" : ` — changed from ${previous}`;
}

function retainerTypeLabel(value, otherDescription = "") {
  if (value === "removable_wire" || value === "hawley") return "Removable Wire";
  if (value === "clear_removable" || value === "essix") return "Clear Removable";
  if (value === "fixed_wire" || value === "fixed_lingual") return "Fixed Wire";
  if (value === "other") {
    const detail = String(otherDescription || "").trim();
    return detail ? `Other: ${detail}` : "Other";
  }
  if (value === "none") return "None";
  return labelize(value || "none");
}

function retainerTypeCost(value, settings) {
  if (value === "removable_wire" || value === "hawley") {
    return Number(settings?.retainer_price_removable_wire_centavos ?? settings?.retainer_price_hawley_centavos ?? 0);
  }
  if (value === "clear_removable" || value === "essix") {
    return Number(settings?.retainer_price_clear_removable_centavos ?? settings?.retainer_price_essix_centavos ?? 0);
  }
  if (value === "fixed_wire" || value === "fixed_lingual") {
    return Number(settings?.retainer_price_fixed_wire_centavos ?? settings?.retainer_price_fixed_lingual_centavos ?? 0);
  }
  if (value === "other") return Number(settings?.retainer_price_other_centavos || 0);
  return 0;
}

function retainerDateLine(label, status, dateValue) {
  const normalized = String(status || "not_needed");
  if (normalized === "not_needed") return `${label}: Not needed`;
  if (dateValue) return `${label}: ${formatActivityDate(dateValue)}`;
  if (normalized === "scheduled") return `${label}: Scheduled`;
  if (normalized === "completed") return `${label}: Completed`;
  return `${label}: Not scheduled`;
}

function paymentMethodLabel(method) {
  if (!method) return "Payment";
  return String(method).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function billingModelLabel(value) {
  const model = ORTHO_BILLING_MODELS.find((option) => option.value === value);
  return model?.label || labelize(value || "per_visit_divisor");
}

function paymentReceipt(payment) {
  if (!payment?.document_year || !payment?.document_number) return "";
  return `R-${payment.document_year}-${String(payment.document_number).padStart(6, "0")}`;
}

function pesosToCentavos(value) {
  const n = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

function centavosToPesoInput(centavos) {
  const value = Number(centavos || 0) / 100;
  return value ? String(Math.round(value)) : "";
}

function compactObject(input) {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

function downPaymentText(billing) {
  const amount = money(billing?.down_payment_centavos || 0);
  const status = labelize(billing?.down_payment_status || "pending");
  return `${amount} · ${status}`;
}

function migratedImportText(row) {
  const paidPrior = Number(row?.historical_payments?.[0]?.total_paid_prior_centavos || 0);
  if (paidPrior > 0) return `${money(paidPrior)} prior paid`;
  return "Imported case";
}

function historicalPaidCentavos(row) {
  return (row?.historical_payments || []).reduce(
    (sum, payment) => sum + Number(payment?.total_paid_prior_centavos || 0),
    0
  );
}

function historicalVisitCount(row) {
  return (row?.historical_visits || []).reduce(
    (sum, visit) => sum + Number(visit?.approximate_visit_count || 0),
    0
  );
}

function orthoVisitsDoneCount(row) {
  const nativeAdjustments = Array.isArray(row?.visits)
    ? row.visits.filter((entry) => (
      entry?.ortho_visit?.visit_type === "adjustment"
      && !entry?.ortho_visit?.voided_at
    )).length
    : 0;
  return nativeAdjustments + historicalVisitCount(row);
}

function orthoOpenBalanceCentavos(row) {
  if (row?.summary?.open_balance_centavos !== undefined && row?.summary?.open_balance_centavos !== null) {
    return Number(row.summary.open_balance_centavos || 0);
  }
  const packagePrice = Number(row?.billing?.package_price_centavos || 0);
  if (packagePrice > 0) {
    const collected = Number(row?.balance?.total_collected_centavos || 0);
    const writeoff = Number(row?.balance?.writeoff_centavos || 0);
    return Math.max(packagePrice - historicalPaidCentavos(row) - collected - writeoff, 0);
  }
  return contractRemainingCentavos(row);
}

function contractRemainingCentavos(row) {
  if (row?.balance?.contract_remaining_centavos !== undefined && row?.balance?.contract_remaining_centavos !== null) {
    return Number(row.balance.contract_remaining_centavos || 0);
  }
  const packagePrice = Number(row?.billing?.package_price_centavos || 0);
  const collected = Number(row?.balance?.total_collected_centavos || 0);
  const writeoff = Number(row?.balance?.writeoff_centavos || 0);
  return Math.max(packagePrice - collected - writeoff, 0);
}

function nextVisitText(row) {
  const c = row?.case || {};
  if (c.status === "completed") return "Completed";
  if (c.status === "abandoned") return "Closed";
  const visits = Array.isArray(row?.visits) ? row.visits : [];
  const latest = visits[visits.length - 1]?.ortho_visit;
  const weeks = latest?.next_visit_interval_weeks;
  if (weeks) return `${weeks}w target`;
  return "Schedule next";
}

function addPhotoNames(e, setForm) {
  const files = Array.from(e.target.files || []);
  setForm((f) => ({
    ...f,
    photoNames: Array.from(new Set([...(f.photoNames || []), ...files.map((file) => file.name)])),
    photoFiles: [...(f.photoFiles || []), ...files],
  }));
  e.target.value = "";
}

function doctorDisplayName(doctor) {
  return formatDoctorName(doctor);
}

function hasReadableDoctorName(doctor) {
  const raw = doctor?.full_name || doctor?.display_name || "";
  return !!raw && !/@/.test(raw) && raw.toLowerCase() !== "doctor";
}

function resolveOrthoPrimaryDoctor({ doctors = [], clinicRole, userId, patientDoctorDefaults }) {
  if (patientDoctorDefaults?.loaded && patientDoctorDefaults.owningDentistId) {
    const owning = doctors.find((doctor) => doctor.id === patientDoctorDefaults.owningDentistId);
    if (owning) return owning;
  }
  if (patientDoctorDefaults?.loaded && patientDoctorDefaults.preferredDoctorId) {
    const preferred = doctors.find((doctor) => doctor.profile_id === patientDoctorDefaults.preferredDoctorId);
    if (preferred) return preferred;
  }
  if (clinicRole === "doctor") {
    const current = doctors.find((doctor) => doctor.profile_id === userId);
    if (current) return current;
  }
  return doctors.find((doctor) => hasReadableDoctorName(doctor)) || doctors[0] || null;
}

export function canCompleteOrthoBilling(settings, clinicRole) {
  const roles = Array.isArray(settings?.billing_setup_roles)
    ? settings.billing_setup_roles
    : ["clinic_admin", "receptionist"];
  if (!clinicRole) return false;
  if (roles.includes(clinicRole)) return true;
  if (clinicRole === "clinic_admin" && (roles.includes("owner") || roles.includes("admin"))) return true;
  if (clinicRole === "receptionist" && (roles.includes("front_desk") || roles.includes("staff"))) return true;
  return false;
}

function labelize(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function OrthoPanel(props) {
  return (
    <FeatureAccessGate featureKey="ortho_module">
      <OrthoPanelBody {...props} />
    </FeatureAccessGate>
  );
}
